/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import {
  saveLead, subscribeLeads,
  updateLeadStatus, updateLeadComments, saveAIInsights, saveLeadReliable, flushPendingLeads, deleteLead, setWhatsappAutoSend, subscribeWhatsappSettings, requestWhatsappSend, requestWhatsappLogout, adminSignIn,
} from "./firebase";
import { sendMail } from "./mailer";
import TrustedPartners from "./TrustedPartners";
import TuapAdmin from "./TuapAdmin";
import RavishaHub from "./RavishaHub";
import { subscribeTuapStudents } from "./tuap";
import { convertLeadToTuap, registerForNTT, nttTestLink } from "./tuap";

/* Internal email to the NM Squad team summarising a new FME lead. */
function buildLeadNotificationEmail(info, scores, leadScores, cls, token) {
  const row = (label, val) =>
    '<tr><td style="padding:7px 14px;font-size:12px;color:#6b7280;border-bottom:1px solid #f1f1f1;white-space:nowrap;">' + label +
    '</td><td style="padding:7px 14px;font-size:13px;font-weight:600;color:#111;border-bottom:1px solid #f1f1f1;">' + (val || '—') + '</td></tr>';
  return '<!DOCTYPE html><html><body style="margin:0;padding:20px;font-family:Arial,sans-serif;background:#f8f9fa;">' +
    '<div style="max-width:560px;margin:0 auto;background:#fff;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;">' +
    '<div style="background:#F5C518;padding:18px 22px;">' +
    '<div style="font-size:11px;color:#7A5F00;letter-spacing:.1em;text-transform:uppercase;">NM Squad · Internal</div>' +
    '<div style="font-size:18px;font-weight:900;color:#1a1a1a;">New First Meeting Enquiry</div>' +
    '<div style="font-size:11px;color:#7A5F00;margin-top:3px;">Token ' + token + ' · ' + cls + '</div></div>' +
    '<table style="width:100%;border-collapse:collapse;">' +
    row('Student', info.studentName) +
    row('Grade', info.grade) +
    row('School', info.school) +
    row('Country / City', (info.country || '—') + ' / ' + (info.city || '—')) +
    row('Student email', info.email) +
    row('Student phone', info.phone) +
    row('Parent email', info.parentEmail) +
    row('Parent phone', info.parentPhone) +
    row('Referral', info.referral) +
    row('Lead person', info.leadPerson) +
    row('IQ / EQ / DQ', scores.IQ + ' / ' + scores.EQ + ' / ' + scores.DQ) +
    row('Personal / Professional', leadScores.personalScore + '% / ' + leadScores.professionalScore + '%') +
    '</table>' +
    '<div style="padding:16px 22px;background:#fffbeb;font-size:12px;color:#7A5F00;">Lead saved to the FME dashboard. Open the admin panel to action it.</div>' +
    '</div></body></html>';
}

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════════ */
const Y = "#F5C518", YD = "#7A5F00";
const BLACK = "#111";
const MID   = "#374151";
const SUB   = "#6b7280";
const BORDER = "#e5e7eb";
const BG    = "#f8fafc";
const DARK  = "#1a1a1a";

const ADMIN_U = "NMSquad2026";
const ADMIN_P = "NMSquadadmin2026@";

const DONE_S = ["Completed (SHIFT)", "Completed (Sign Up)"];

const STATUS_OPS = [
  "Fresh Enquiry",
  "Connected",
  "Process of Paying",
  "NTT in progress",
  "FM scheduled",
  "NTT feedback scheduled",
  "Completed (SHIFT)",
  "Completed (Sign Up)",
  "No Response",
  "Reviewing information",
  "Yet to decide",
];

const COUNTRIES = [
  "India 🇮🇳","United States 🇺🇸","United Kingdom 🇬🇧","Canada 🇨🇦","Australia 🇦🇺",
  "UAE 🇦🇪","Singapore 🇸🇬","New Zealand 🇳🇿","Germany 🇩🇪","Netherlands 🇳🇱",
  "Hong Kong 🇭🇰","Saudi Arabia 🇸🇦","Qatar 🇶🇦","Bahrain 🇧🇭","Kuwait 🇰🇼",
  "South Africa 🇿🇦","Kenya 🇰🇪","Nigeria 🇳🇬","Ireland 🇮🇪","Sweden 🇸🇪",
  "Switzerland 🇨🇭","France 🇫🇷","Japan 🇯🇵","Malaysia 🇲🇾","Other 🌍",
];

const GRADES = ["Grade 8","Grade 9","Grade 10","Grade 11","Grade 12","Transfer Student","Graduate Student","Other"];;

// Convert a 2-letter ISO country code into its flag emoji (regional indicator symbols).
function flagEmoji(iso) {
  if (!iso || iso.length !== 2 || !/^[A-Za-z]{2}$/.test(iso)) return "🌐";
  return iso.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

// Priority countries first (NM Squad's most common), then the rest of the world alphabetically.
const COUNTRY_CODES = [
  // — Most common —
  { code:"+91",  flag:"IN", name:"India" },
  { code:"+1",   flag:"US", name:"USA / Canada" },
  { code:"+44",  flag:"GB", name:"UK" },
  { code:"+61",  flag:"AU", name:"Australia" },
  { code:"+971", flag:"AE", name:"UAE" },
  { code:"+65",  flag:"SG", name:"Singapore" },
  { code:"+64",  flag:"NZ", name:"New Zealand" },
  { code:"+49",  flag:"DE", name:"Germany" },
  { code:"+31",  flag:"NL", name:"Netherlands" },
  { code:"+852", flag:"HK", name:"Hong Kong" },
  { code:"+966", flag:"SA", name:"Saudi Arabia" },
  { code:"+974", flag:"QA", name:"Qatar" },
  { code:"+973", flag:"BH", name:"Bahrain" },
  { code:"+965", flag:"KW", name:"Kuwait" },
  { code:"+968", flag:"OM", name:"Oman" },
  { code:"+27",  flag:"ZA", name:"South Africa" },
  { code:"+254", flag:"KE", name:"Kenya" },
  { code:"+234", flag:"NG", name:"Nigeria" },
  { code:"+353", flag:"IE", name:"Ireland" },
  { code:"+46",  flag:"SE", name:"Sweden" },
  { code:"+41",  flag:"CH", name:"Switzerland" },
  { code:"+33",  flag:"FR", name:"France" },
  { code:"+81",  flag:"JP", name:"Japan" },
  { code:"+60",  flag:"MY", name:"Malaysia" },
  // — Rest of the world (A–Z) —
  { code:"+93",  flag:"AF", name:"Afghanistan" },
  { code:"+355", flag:"AL", name:"Albania" },
  { code:"+213", flag:"DZ", name:"Algeria" },
  { code:"+376", flag:"AD", name:"Andorra" },
  { code:"+244", flag:"AO", name:"Angola" },
  { code:"+54",  flag:"AR", name:"Argentina" },
  { code:"+374", flag:"AM", name:"Armenia" },
  { code:"+43",  flag:"AT", name:"Austria" },
  { code:"+994", flag:"AZ", name:"Azerbaijan" },
  { code:"+880", flag:"BD", name:"Bangladesh" },
  { code:"+375", flag:"BY", name:"Belarus" },
  { code:"+32",  flag:"BE", name:"Belgium" },
  { code:"+501", flag:"BZ", name:"Belize" },
  { code:"+229", flag:"BJ", name:"Benin" },
  { code:"+975", flag:"BT", name:"Bhutan" },
  { code:"+591", flag:"BO", name:"Bolivia" },
  { code:"+387", flag:"BA", name:"Bosnia & Herzegovina" },
  { code:"+267", flag:"BW", name:"Botswana" },
  { code:"+55",  flag:"BR", name:"Brazil" },
  { code:"+673", flag:"BN", name:"Brunei" },
  { code:"+359", flag:"BG", name:"Bulgaria" },
  { code:"+226", flag:"BF", name:"Burkina Faso" },
  { code:"+257", flag:"BI", name:"Burundi" },
  { code:"+855", flag:"KH", name:"Cambodia" },
  { code:"+237", flag:"CM", name:"Cameroon" },
  { code:"+238", flag:"CV", name:"Cape Verde" },
  { code:"+236", flag:"CF", name:"Central African Republic" },
  { code:"+235", flag:"TD", name:"Chad" },
  { code:"+56",  flag:"CL", name:"Chile" },
  { code:"+86",  flag:"CN", name:"China" },
  { code:"+57",  flag:"CO", name:"Colombia" },
  { code:"+269", flag:"KM", name:"Comoros" },
  { code:"+242", flag:"CG", name:"Congo (Republic)" },
  { code:"+243", flag:"CD", name:"Congo (DR)" },
  { code:"+506", flag:"CR", name:"Costa Rica" },
  { code:"+385", flag:"HR", name:"Croatia" },
  { code:"+53",  flag:"CU", name:"Cuba" },
  { code:"+357", flag:"CY", name:"Cyprus" },
  { code:"+420", flag:"CZ", name:"Czechia" },
  { code:"+45",  flag:"DK", name:"Denmark" },
  { code:"+253", flag:"DJ", name:"Djibouti" },
  { code:"+593", flag:"EC", name:"Ecuador" },
  { code:"+20",  flag:"EG", name:"Egypt" },
  { code:"+503", flag:"SV", name:"El Salvador" },
  { code:"+240", flag:"GQ", name:"Equatorial Guinea" },
  { code:"+291", flag:"ER", name:"Eritrea" },
  { code:"+372", flag:"EE", name:"Estonia" },
  { code:"+268", flag:"SZ", name:"Eswatini" },
  { code:"+251", flag:"ET", name:"Ethiopia" },
  { code:"+679", flag:"FJ", name:"Fiji" },
  { code:"+358", flag:"FI", name:"Finland" },
  { code:"+241", flag:"GA", name:"Gabon" },
  { code:"+220", flag:"GM", name:"Gambia" },
  { code:"+995", flag:"GE", name:"Georgia" },
  { code:"+233", flag:"GH", name:"Ghana" },
  { code:"+30",  flag:"GR", name:"Greece" },
  { code:"+299", flag:"GL", name:"Greenland" },
  { code:"+502", flag:"GT", name:"Guatemala" },
  { code:"+224", flag:"GN", name:"Guinea" },
  { code:"+245", flag:"GW", name:"Guinea-Bissau" },
  { code:"+592", flag:"GY", name:"Guyana" },
  { code:"+509", flag:"HT", name:"Haiti" },
  { code:"+504", flag:"HN", name:"Honduras" },
  { code:"+36",  flag:"HU", name:"Hungary" },
  { code:"+354", flag:"IS", name:"Iceland" },
  { code:"+62",  flag:"ID", name:"Indonesia" },
  { code:"+98",  flag:"IR", name:"Iran" },
  { code:"+964", flag:"IQ", name:"Iraq" },
  { code:"+972", flag:"IL", name:"Israel" },
  { code:"+39",  flag:"IT", name:"Italy" },
  { code:"+225", flag:"CI", name:"Ivory Coast" },
  { code:"+962", flag:"JO", name:"Jordan" },
  { code:"+7",   flag:"RU", name:"Russia / Kazakhstan" },
  { code:"+856", flag:"LA", name:"Laos" },
  { code:"+371", flag:"LV", name:"Latvia" },
  { code:"+961", flag:"LB", name:"Lebanon" },
  { code:"+266", flag:"LS", name:"Lesotho" },
  { code:"+231", flag:"LR", name:"Liberia" },
  { code:"+218", flag:"LY", name:"Libya" },
  { code:"+423", flag:"LI", name:"Liechtenstein" },
  { code:"+370", flag:"LT", name:"Lithuania" },
  { code:"+352", flag:"LU", name:"Luxembourg" },
  { code:"+853", flag:"MO", name:"Macau" },
  { code:"+261", flag:"MG", name:"Madagascar" },
  { code:"+265", flag:"MW", name:"Malawi" },
  { code:"+960", flag:"MV", name:"Maldives" },
  { code:"+223", flag:"ML", name:"Mali" },
  { code:"+356", flag:"MT", name:"Malta" },
  { code:"+222", flag:"MR", name:"Mauritania" },
  { code:"+230", flag:"MU", name:"Mauritius" },
  { code:"+52",  flag:"MX", name:"Mexico" },
  { code:"+373", flag:"MD", name:"Moldova" },
  { code:"+377", flag:"MC", name:"Monaco" },
  { code:"+976", flag:"MN", name:"Mongolia" },
  { code:"+382", flag:"ME", name:"Montenegro" },
  { code:"+212", flag:"MA", name:"Morocco" },
  { code:"+258", flag:"MZ", name:"Mozambique" },
  { code:"+95",  flag:"MM", name:"Myanmar" },
  { code:"+264", flag:"NA", name:"Namibia" },
  { code:"+977", flag:"NP", name:"Nepal" },
  { code:"+505", flag:"NI", name:"Nicaragua" },
  { code:"+227", flag:"NE", name:"Niger" },
  { code:"+389", flag:"MK", name:"North Macedonia" },
  { code:"+47",  flag:"NO", name:"Norway" },
  { code:"+92",  flag:"PK", name:"Pakistan" },
  { code:"+970", flag:"PS", name:"Palestine" },
  { code:"+507", flag:"PA", name:"Panama" },
  { code:"+675", flag:"PG", name:"Papua New Guinea" },
  { code:"+595", flag:"PY", name:"Paraguay" },
  { code:"+51",  flag:"PE", name:"Peru" },
  { code:"+63",  flag:"PH", name:"Philippines" },
  { code:"+48",  flag:"PL", name:"Poland" },
  { code:"+351", flag:"PT", name:"Portugal" },
  { code:"+40",  flag:"RO", name:"Romania" },
  { code:"+250", flag:"RW", name:"Rwanda" },
  { code:"+685", flag:"WS", name:"Samoa" },
  { code:"+378", flag:"SM", name:"San Marino" },
  { code:"+239", flag:"ST", name:"São Tomé & Príncipe" },
  { code:"+221", flag:"SN", name:"Senegal" },
  { code:"+381", flag:"RS", name:"Serbia" },
  { code:"+248", flag:"SC", name:"Seychelles" },
  { code:"+232", flag:"SL", name:"Sierra Leone" },
  { code:"+421", flag:"SK", name:"Slovakia" },
  { code:"+386", flag:"SI", name:"Slovenia" },
  { code:"+252", flag:"SO", name:"Somalia" },
  { code:"+82",  flag:"KR", name:"South Korea" },
  { code:"+211", flag:"SS", name:"South Sudan" },
  { code:"+34",  flag:"ES", name:"Spain" },
  { code:"+94",  flag:"LK", name:"Sri Lanka" },
  { code:"+249", flag:"SD", name:"Sudan" },
  { code:"+597", flag:"SR", name:"Suriname" },
  { code:"+963", flag:"SY", name:"Syria" },
  { code:"+886", flag:"TW", name:"Taiwan" },
  { code:"+992", flag:"TJ", name:"Tajikistan" },
  { code:"+255", flag:"TZ", name:"Tanzania" },
  { code:"+66",  flag:"TH", name:"Thailand" },
  { code:"+228", flag:"TG", name:"Togo" },
  { code:"+676", flag:"TO", name:"Tonga" },
  { code:"+216", flag:"TN", name:"Tunisia" },
  { code:"+90",  flag:"TR", name:"Turkey" },
  { code:"+993", flag:"TM", name:"Turkmenistan" },
  { code:"+256", flag:"UG", name:"Uganda" },
  { code:"+380", flag:"UA", name:"Ukraine" },
  { code:"+598", flag:"UY", name:"Uruguay" },
  { code:"+998", flag:"UZ", name:"Uzbekistan" },
  { code:"+678", flag:"VU", name:"Vanuatu" },
  { code:"+379", flag:"VA", name:"Vatican City" },
  { code:"+58",  flag:"VE", name:"Venezuela" },
  { code:"+84",  flag:"VN", name:"Vietnam" },
  { code:"+967", flag:"YE", name:"Yemen" },
  { code:"+260", flag:"ZM", name:"Zambia" },
  { code:"+263", flag:"ZW", name:"Zimbabwe" },
  { code:"+383", flag:"XK", name:"Kosovo" },
  { code:"+other",flag:"--", name:"Other (enter code)" },
];

const PHONE_RULES = {
  '+91':    { regex:/^[6-9]\d{9}$/, label:'10 digits starting with 6-9' },
  '+1':     { regex:/^[2-9]\d{2}[2-9]\d{6}$/, label:'10 valid NANP digits' },
  '+44':    { regex:/^7\d{9}$/, label:'10 digits starting with 7' },
  '+61':    { regex:/^4\d{8}$/, label:'9 digits starting with 4' },
  '+971':   { regex:/^5\d{8}$/, label:'9 digits starting with 5' },
  '+65':    { regex:/^[689]\d{7}$/, label:'8 digits starting with 6, 8 or 9' },
  '+64':    { regex:/^2\d{7,8}$/, label:'8-9 digits starting with 2' },
  '+49':    { regex:/^1[5-7]\d{8,9}$/, label:'10-11 digits starting with 15/16/17' },
  '+31':    { regex:/^6\d{8}$/, label:'9 digits starting with 6' },
  '+852':   { regex:/^[2-9]\d{7}$/, label:'8 digits' },
  '+966':   { regex:/^5\d{8}$/, label:'9 digits starting with 5' },
  '+974':   { regex:/^[3-7]\d{7}$/, label:'8 digits starting with 3-7' },
  '+973':   { regex:/^[3-9]\d{7}$/, label:'8 digits starting with 3-9' },
  '+965':   { regex:/^[569]\d{7}$/, label:'8 digits starting with 5, 6 or 9' },
  '+27':    { regex:/^[6-8]\d{8}$/, label:'9 digits starting with 6-8' },
  '+254':   { regex:/^(7|1)\d{8}$/, label:'9 digits starting with 7 or 1' },
  '+234':   { regex:/^[7-9]\d{9}$/, label:'10 digits starting with 7-9' },
  '+353':   { regex:/^8\d{8}$/, label:'9 digits starting with 8' },
  '+46':    { regex:/^7\d{6,8}$/, label:'7-9 digits starting with 7' },
  '+41':    { regex:/^7\d{8}$/, label:'9 digits starting with 7' },
  '+33':    { regex:/^[67]\d{8}$/, label:'9 digits starting with 6 or 7' },
  '+81':    { regex:/^(70|80|90)\d{8}$/, label:'10 digits starting with 70/80/90' },
  '+60':    { regex:/^1\d{8,9}$/, label:'9-10 digits starting with 1' },
  '+other': { regex:/^\d{6,14}$/, label:'6-14 digits' },
};

function isRepeating(d) { return /^(\d)\1{6,}$/.test(d); }
function isSequential(d) {
  const seqs = ['0123456789','9876543210','1234567890'];
  return seqs.some(s => d.includes(s.slice(0,8)));
}

function validatePhone(n, cc) {
  const d = (n||'').replace(/\D/g,'');
  if (!d) return false;
  if (isRepeating(d)) return false;
  if (isSequential(d)) return false;
  const rule = PHONE_RULES[cc||'+other'] || PHONE_RULES['+other'];
  return rule.regex.test(d);
}

function phoneHint(n, cc) {
  const d = (n||'').replace(/\D/g,'');
  if (!d) return '';
  if (isRepeating(d)) return 'Repeating digits not allowed';
  if (isSequential(d)) return 'Sequential numbers not allowed';
  const rule = PHONE_RULES[cc||'+other'] || PHONE_RULES['+other'];
  if (!rule.regex.test(d)) return 'Format: ' + rule.label;
  return '';
}

function fullPhone(cc, num, custom) {
  let code = cc==='+other' ? (custom||'').trim() : cc;
  if (code && !code.startsWith('+')) code = '+' + code.replace(/\D/g,'');
  return code + (num||'').replace(/\D/g,'');
}

const ABOUT_NM = `As someone who cares deeply about education, Neeraj Mandhana has been guiding high school students toward meaningful career planning and global college admissions since 2010. For over a decade, NM has worked at the intersection of students, parents, schools, and universities, helping young people make informed decisions grounded in clarity, self-understanding, and long-term outcomes. He actively mentors both individual students and fellow counseling professionals, and is widely sought after for his practical, reality-based insights into admissions and career pathways.

Each year, NM travels internationally to visit leading universities and attend global education conferences, staying closely connected to evolving admissions practices and educational trends. College admissions officers regularly seek his perspective on the South Asian education landscape, while fellow professionals engage with him on strengthening ethical and professional standards within the counseling sector.`;

/* ═══════════════════════════════════════════════════════════════
   INTEREST MAP  — exact deterministic mapping, A-Z
   Removed: Painting (duplicate), Make Up
═══════════════════════════════════════════════════════════════ */
const INTEREST_MAP = [
  { label:"Acting, Theatre, Public Speaking", q:["EQ"] },
  { label:"Aeromodelling",                    q:["IQ"] },
  { label:"Art, Painting",                    q:["DQ"] },
  { label:"Astronomy",                        q:["IQ"] },
  { label:"Cartoons, Comics, Anime",          q:["DQ"] },
  { label:"Chess",                            q:["IQ"] },
  { label:"Coding, AI",                       q:["IQ"] },
  { label:"Community Service",                q:["EQ"] },
  { label:"Dance, DJ",                        q:["EQ"] },
  { label:"Doodling, Calligraphy",            q:["DQ"] },
  { label:"Fashion, Makeup",                  q:["DQ"] },
  { label:"Film Making, Video Editing",       q:["DQ"] },
  { label:"Films/TVShows",                    q:["DQ"] },
  { label:"Food, Cooking or Eating Out",      q:["EQ"] },
  { label:"Gaming & Animation",               q:["DQ"] },
  { label:"Gardening",                        q:["EQ"] },
  { label:"Interior & Home Decor",            q:["DQ"] },
  { label:"Lego, Gadgets",                    q:["IQ"] },
  { label:"Making New Friends, Hanging Out",  q:["EQ"] },
  { label:"Mathematics",                      q:["IQ"] },
  { label:"Music - Playing, Singing, Listening", q:["EQ"] },
  { label:"Origami",                          q:["IQ"] },
  { label:"Pet Care",                         q:["EQ"] },
  { label:"Photography",                      q:["DQ"] },
  { label:"Puzzles, Quizzing",                q:["IQ"] },
  { label:"Reading, Writing",                 q:["EQ"] },
  { label:"Robotics",                         q:["IQ"] },
  { label:"Rubik's Cube",                    q:["IQ"] },
  { label:"Sculpture/Pottery",                q:["DQ"] },
  { label:"Travelling",                       q:["EQ"] },
];

const ITEMS_PP = 10;

/* ═══════════════════════════════════════════════════════════════
   PARENT QUESTIONS
═══════════════════════════════════════════════════════════════ */
const PARENT_QS = [
  {
    id:"pq1", scored:true,
    q:"Which is more important to you?",
    opts:[
      { text:"Personalised one-to-one mentorship by Dr. Neeraj Mandhana to groom your child", type:"personal" },
      { text:"Support with college admissions and application outcomes",       type:"professional" },
    ],
  },
  {
    id:"pq2", scored:true,
    q:"Which one would you prefer for your child?",
    opts:[
      { text:"A college where your child can truly thrive and find the right academic and personal fit", type:"personal" },
      { text:"A highly ranked institution such as an Ivy League or similarly prestigious university",    type:"professional" },
    ],
  },
  {
    id:"pq3", scored:true,
    q:"Which one should we put our maximum energy into?",
    opts:[
      { text:"Identifying the most suitable college major, academic programme and country", type:"personal" },
      { text:"Building a strong and distinctive student profile",                           type:"professional" },
      { text:"Preparing compelling college essays and interview responses",                 type:"personal" },
      { text:"Consistent mentorship and weekly motivation for your child",                  type:"personal" },
    ],
  },
  {
    id:"pq4", scored:false,
    q:"Who will take the lead in engaging with us?",
    opts:[
      { text:"Mother",  type:"personal" },
      { text:"Father",  type:"personal" },
      { text:"Student", type:"personal" },
      { text:"Other",   type:"personal" },
    ],
  },
  {
    id:"pq5", scored:true,
    q:"What most influences the way you think about education and college admissions?",
    opts:[
      { text:"Your own educational background and global exposure through travel",    type:"personal" },
      { text:"Information encountered through WhatsApp forwards or Instagram reels",  type:"professional" },
      { text:"Visiting colleges and speaking with current students or alumni",        type:"personal" },
      { text:"Independent online research",                                           type:"personal" },
    ],
  },
  {
    id:"pq6", scored:true,
    q:"Which professional engagement does college counselling most closely resemble?",
    opts:[
      { text:"Private Tuitions",       type:"professional" },
      { text:"Accountant",             type:"professional" },
      { text:"Management Consultant",  type:"professional" },
      { text:"Doctor",                 type:"personal" },
      { text:"Lawyer",                 type:"personal" },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════
   SCORING — 100% DETERMINISTIC
   Interested = 2 pts, Maybe = 1 pt, Not = 0 pts
   Score% = (raw / max_possible_for_that_category) × 100
═══════════════════════════════════════════════════════════════ */
function calculateScores(interests) {
  const raw = { IQ:0, EQ:0, DQ:0, PQ:0 };
  INTEREST_MAP.forEach(item => {
    const val    = interests[item.label];
    const points = val === "interested" ? 2 : val === "maybe" ? 1 : 0;
    item.q.forEach(cat => { raw[cat] += points; });
  });
  const total = raw.IQ + raw.EQ + raw.DQ || 1;
  const IQpct = Math.round((raw.IQ / total) * 100);
  const EQpct = Math.round((raw.EQ / total) * 100);
  const DQpct = Math.round((raw.DQ / total) * 100);
  const logicPct  = IQpct;
  const peoplePct = EQpct + DQpct;
  const dominant  = raw.IQ >= raw.EQ && raw.IQ >= raw.DQ ? "IQ"
                  : raw.EQ >= raw.DQ ? "EQ" : "DQ";
  const topInterests = INTEREST_MAP
    .filter(i => interests[i.label] === "interested")
    .map(i => i.label);
  return { raw, IQ:IQpct, EQ:EQpct, DQ:DQpct, AQ:0,
           logicPct, peoplePct, dominant, topInterests };
}

function calculateGuidance(parentAns) {
  let personal = 0, professional = 0;
  PARENT_QS.forEach(pq => {
    if (!pq.scored) return;
    const opt = pq.opts.find(o => o.text === parentAns[pq.id]);
    if (!opt) return;
    opt.type === "personal" ? personal++ : professional++;
  });
  const total = personal + professional || 1;
  const personalPct  = Math.round((personal  / total) * 100);
  const convScore    = personalPct /* personal clients convert more */;
  return {
    personal, professional,
    personalScore: personalPct,
    professionalScore: 100 - personalPct,
    conversionScore: convScore,
  };
}

function classifyLead(score) {
  if (score >= 80) return { label:"HOT",     emoji:"🔥", color:"#dc2626", bg:"#fef2f2" };
  if (score >= 60) return { label:"WARM",    emoji:"⚡", color:"#d97706", bg:"#fffbeb" };
  if (score >= 40) return { label:"NURTURE", emoji:"🌱", color:"#16a34a", bg:"#f0fdf4" };
  return               { label:"COLD",       emoji:"❄️", color:"#6b7280", bg:"#f9fafb" };
}

/* ═══════════════════════════════════════════════════════════════
   AI (auto, 25-second hard timeout)
═══════════════════════════════════════════════════════════════ */
async function generateAI(sub) {
  const sc = sub.scores || {};
  const ld = sub.leadScores || {};
  const pAnswers = PARENT_QS.map(q => `${q.q}: ${sub.parentAns?.[q.id]||"N/A"}`).join("\n");

  const prompt = `You are an elite education sales strategist for NM Squad (Neeraj Mandhana).

STUDENT: ${sub.info?.studentName}, Grade: ${sub.info?.grade}, ${sub.info?.country}
Referred by: ${sub.info?.referral || "N/A"}
Lead person: ${sub.info?.leadPerson || "N/A"}

SCORES (% of maximum possible in each category):
IQ (Analytical): ${sc.IQ}%
EQ (Emotional/Social): ${sc.EQ}%
DQ (Creative/Design): ${sc.DQ}%
AQ (Physical/Action): ${sc.AQ}%
Dominant: ${sc.dominant}

GUIDANCE PREFERENCE:
Personal mentorship: ${ld.personalScore}%
Professional outcome: ${ld.professionalScore}%
Conversion score: ${ld.conversionScore}%

PARENT ANSWERS:
${pAnswers}

Return ONLY valid JSON, no markdown:
{
  "personalityType": "Introvert / Extrovert / Ambivert",
  "academicOrientation": "STEM / Business / Creative / Mixed",
  "dominantStrengthExplanation": "2 sentences about their top strength",
  "likelyCareers": ["career1","career2","career3"],
  "parentType": "Emotional / Logical / Prestige-driven / Confused / ROI-focused",
  "buyingBehavior": "what will make them say yes",
  "likelyObjections": ["objection1","objection2"],
  "rajibStrategy": "Approach: [how to open]. Emphasise: [what to highlight]. Avoid: [what NOT to say]. Pitch angle: [exact angle]. Closing line: [exact sentence].",
  "urgencyNote": "one sentence why they should act now"
}`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 25000);

  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        messages: [{ role:"user", content: prompt }],
      }),
    });
    clearTimeout(timeout);
    if (!res.ok) throw new Error(`API ${res.status}`);
    const data = await res.json();
    const txt  = (data.content||[]).map(b=>b.text||"").join("");
    const m    = txt.match(/\{[\s\S]*\}/);
    if (!m) throw new Error("No JSON");
    return JSON.parse(m[0]);
  } catch(e) {
    clearTimeout(timeout);
    throw e;
  }
}

/* ═══════════════════════════════════════════════════════════════
   PDF using html2canvas + jsPDF
═══════════════════════════════════════════════════════════════ */
async function loadScript(src) {
  return new Promise((res,rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
}

async function generatePDF(elementId, filename) {
  try {
    await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
    await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

    const el = document.getElementById(elementId);
    if (!el) return;

    const canvas  = await window.html2canvas(el, { scale:2, useCORS:true, backgroundColor:"#ffffff" });
    const imgData = canvas.toDataURL("image/png");
    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF("p","mm","a4");

    const pageW  = 210;
    const pageH  = 297;
    const imgH   = (canvas.height * pageW) / canvas.width;
    let   yPos   = 0;
    let   remaining = imgH;

    while (remaining > 0) {
      pdf.addImage(imgData, "PNG", 0, -yPos, pageW, imgH);
      remaining -= pageH;
      yPos      += pageH;
      if (remaining > 0) pdf.addPage();
    }
    pdf.save(filename);
  } catch(e) {
    alert("PDF tip: Right-click → Print → Save as PDF for best results.");
  }
}

/* ═══════════════════════════════════════════════════════════════
   STATUS COLOUR
═══════════════════════════════════════════════════════════════ */
function statusColor(s) {
  const m = {
    "Fresh Enquiry":"#6b7280", "Connected":"#2563eb", "Process of Paying":"#7c3aed", "NTT in progress":"#0891b2", "FM scheduled":"#0d9488", "NTT feedback scheduled":"#ca8a04", "Completed (SHIFT)":"#16a34a", "Completed (Sign Up)":"#15803d", "No Response":"#dc2626", "Reviewing information":"#ea580c", "Yet to decide":"#9333ea",
  };
  return m[s]||"#6b7280";
}

/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE
═══════════════════════════════════════════════════════════════ */
function useWidth() {
  const [w,setW] = useState(typeof window!=="undefined"?window.innerWidth:1200);
  useEffect(()=>{
    const h=()=>setW(window.innerWidth);
    window.addEventListener("resize",h);
    return ()=>window.removeEventListener("resize",h);
  },[]);
  return w;
}

/* ═══════════════════════════════════════════════════════════════
   SHARED UI
═══════════════════════════════════════════════════════════════ */
function NMLogo({size=52}) {
  return (
    <img src="/nm-logo.png" alt="NM Squad" width={size} height={size}
      style={{flexShrink:0,objectFit:"contain"}}
      onError={e=>{e.target.style.display='none';}}/>
  );
}

function Card({children,style}) {
  return <div style={{background:"#fff",border:`1px solid ${BORDER}`,borderRadius:12,padding:"20px 22px",marginBottom:14,...style}}>{children}</div>;
}

function Btn({onClick,disabled,children,color="yellow",full,size="md"}) {
  const p = size==="sm"?"8px 14px":"13px 22px", f=size==="sm"?13:15;
  const map = {
    yellow: {bg:disabled?"#e5e7eb":Y, text:disabled?"#9ca3af":YD},
    dark:   {bg:DARK, text:"#fff"},
    green:  {bg:"#16a34a",text:"#fff"},
    wa:     {bg:"#25D366",text:"#fff"},
    outline:{bg:"#fff",text:MID,border:`1px solid ${BORDER}`},
    red:    {bg:"#dc2626",text:"#fff"},
  };
  const st = map[color]||map.yellow;
  return (
    <button onClick={disabled?undefined:onClick} style={{
      display:"inline-block", width:full?"100%":"auto", padding:p,
      boxSizing:"border-box", background:st.bg, color:st.text,
      border:st.border||"none", borderRadius:8, fontSize:f,
      fontWeight:700, cursor:disabled?"not-allowed":"pointer",
    }}>{children}</button>
  );
}

function Prog({current,total,label}) {
  const pct = total ? Math.round((current/total)*100) : 0;
  return (
    <div style={{marginBottom:22}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:7,fontSize:14,color:MID}}>
        <span>{label}</span><span style={{fontWeight:700,color:BLACK}}>{current}/{total}</span>
      </div>
      <div style={{height:7,background:"#e5e7eb",borderRadius:9999}}>
        <div style={{height:"100%",width:`${pct}%`,background:Y,borderRadius:9999,transition:"width .3s"}}/>
      </div>
    </div>
  );
}

function StepDot({step,cur,label}) {
  const done=cur>step, active=cur===step;
  return (
    <div style={{display:"flex",alignItems:"center",gap:7}}>
      <div style={{width:26,height:26,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0,background:done?Y:active?DARK:"#e5e7eb",color:done?YD:active?"#fff":"#9ca3af"}}>{done?"✓":step}</div>
      <span style={{fontSize:13,color:active?BLACK:"#9ca3af",fontWeight:active?700:400}}>{label}</span>
    </div>
  );
}

function Steps({cur}) {
  return (
    <div style={{display:"flex",gap:16,marginBottom:24,flexWrap:"wrap"}}>
      {[["1","Interests"],["2","Parents"]].map(([n,l],i)=>(
        <StepDot key={n} step={i+1} cur={cur} label={l}/>
      ))}
    </div>
  );
}

function SectionTitle({children}) {
  return <div style={{fontSize:11,fontWeight:700,color:YD,letterSpacing:"0.08em",textTransform:"uppercase",marginTop:20,marginBottom:10,borderBottom:`2px solid ${Y}`,paddingBottom:4}}>{children}</div>;
}

function GuidancePie({personal,size=200}) {
  const pro  = 100-personal;
  const data = [
    {name:`Personal ${personal}%`, value:personal||1},
    {name:`Professional ${pro}%`,  value:pro||1},
  ];
  return (
    <div style={{textAlign:"center"}}>
      <div style={{fontSize:13,fontWeight:700,color:BLACK,marginBottom:6}}>Guidance Preference</div>
      <ResponsiveContainer width="100%" height={size}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={size*.25} outerRadius={size*.38} dataKey="value" startAngle={90} endAngle={-270}>
            <Cell fill={Y}/><Cell fill="#e5e7eb"/>
          </Pie>
          <Tooltip formatter={v=>`${v}%`}/>
          <Legend iconType="circle" wrapperStyle={{fontSize:13,color:BLACK}}/>
        </PieChart>
      </ResponsiveContainer>
      <p style={{margin:0,fontSize:12,color:SUB,lineHeight:1.6}}>Reflects how the family prefers to receive guidance.</p>
    </div>
  );
}

const INP = {width:"100%",boxSizing:"border-box",background:"#f9fafb",color:BLACK,border:`1px solid ${BORDER}`,borderRadius:8,padding:"12px 14px",fontSize:15,outline:"none",fontFamily:"sans-serif"};
const SEL = {...INP,appearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23374151' strokeWidth='1.5' fill='none'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 14px center",paddingRight:36};

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="white" style={{flexShrink:0}}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
    <path d="M11.5 2C6.252 2 2 6.252 2 11.5c0 1.87.504 3.627 1.39 5.142L2 22l5.47-1.373A9.456 9.456 0 0011.5 21C16.748 21 21 16.748 21 11.5S16.748 2 11.5 2zm0 17.25c-1.71 0-3.306-.485-4.659-1.327l-3.25.816.84-3.168A7.706 7.706 0 013.75 11.5c0-4.273 3.477-7.75 7.75-7.75S19.25 7.227 19.25 11.5 15.773 19.25 11.5 19.25z" fillRule="evenodd"/>
  </svg>
);

const PCSS = `@page{size:A4;margin:12mm}@media print{.np{display:none!important}body{background:#fff!important}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}}`;

/* ═══════════════════════════════════════════════════════════════
   MAIN APP
═══════════════════════════════════════════════════════════════ */
/* ── Path ↔ view mapping for admin routes ─────────────────────────────────
   Friendly aliases /whatsapp, /communication(s), /trusted-partners all
   resolve to their canonical /admin/* sibling so links keep working. */
const ADMIN_VIEW_TO_PATH = {
  admin: "/admin",
  adminDetail: "/admin",
  adminLogin: "/admin",
  tuap: "/admin/tuap",
  partners: "/admin/partners",
  communications: "/admin/communications",
};
function parseAdminPath(pathname) {
  const p = (pathname || "").replace(/^\/+|\/+$/g, "").toLowerCase();
  if (p === "admin")                                           return "admin";
  if (p === "admin/tuap" || p === "tuap")                       return "tuap";
  if (p === "admin/partners" || p === "trusted-partners" || p === "partners") return "partners";
  if (p === "admin/communications" || p === "admin/communication"
      || p === "communication" || p === "communications" || p === "whatsapp") return "communications";
  return null;
}
function initialAdminView() {
  const want = parseAdminPath(window.location.pathname);
  if (!want) return null;
  const authed = localStorage.getItem("nmAdminAuth") === "1";
  return authed ? want : "adminLogin";
}

/* Lightweight wrapper that fetches the TUAP student list itself, so the
   Communications hub can render at /admin/communications without going
   through TuapAdmin. */
function RavishaHubStandalone({ onBack }) {
  const [students, setStudents] = useState([]);
  useEffect(() => subscribeTuapStudents(setStudents, () => {}), []);
  return <RavishaHub students={students} onBack={onBack} />;
}

export default function NTTApp() {
  /* ── Form state ── */
  const [view,       setView]      = useState(() => initialAdminView() || "welcome");
  const [info,       setInfo]      = useState({
    studentName:"", email:"", phone:"", school:"",
    parentEmail:"", parentPhone:"", studentCC:"+91", parentCC:"+91",
    studentCCCustom:"", parentCCCustom:"",
    country:"", countryOther:"", city:"",
    grade:"", gender:"", genderOther:"", gradeOther:"", referral:"",
  });
  const [interests,  setInterests] = useState({});
  const [parentAns,  setParentAns] = useState({});
  const [pq4Other,   setPq4Other]  = useState("");
  const [page,       setPage]      = useState(0);
  const [report,     setReport]    = useState(null);
  const [pdfLoading, setPdfL]      = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [spTouched, setSpTouched] = useState(false);
  const [ppTouched, setPpTouched] = useState(false);

  /* ── Admin state ── */
  const [adU,     setAdU]     = useState("");
  const [adP,     setAdP]     = useState("");
  const [adErr,   setAdErr]   = useState("");
  const [adTab,   setAdTab]   = useState("inbox");
  const [sort,    setSort]    = useState({key:"date",dir:-1});   // leads table sort
  const [searchQ, setSearchQ] = useState("");
  const [leads,   setLeads]   = useState([]);
  const [detailId,setDetailId]= useState(null);
  const [aiData,  setAiData]  = useState(null);
  const [aiLoad,  setAiLoad]  = useState(false);
  const [waSettings, setWaSettings] = useState({ autoSend:false, status:"disconnected", qrCode:"", qrData:"" });
  const [showQR, setShowQR] = useState(false);
  
  const [sendingWA, setSendingWA] = useState({});
  const [nttBusy, setNttBusy] = useState({});
  const unsubRef = useRef(null);

  /* ── Responsive ── */
  const ww  = useWidth();
  const mob = ww < 640;

  /* ── Computed ── */
  const totalPages   = Math.ceil(INTEREST_MAP.length / ITEMS_PP);
  const pageItems    = INTEREST_MAP.slice(page*ITEMS_PP,(page+1)*ITEMS_PP);
  const answeredCnt  = Object.keys(interests).length;
  const cur          = view==="interests"?1:view==="parentQ"?2:0;

  const effCountry   = info.country==="Other 🌍" ? info.countryOther : info.country;
  const effGrade     = info.grade==="Other"       ? info.gradeOther  : info.grade;
  const effGender    = info.gender==="Other"      ? (info.genderOther||"Other") : info.gender;
  const pq4Val       = parentAns.pq4||"";
  const pq4Ok        = pq4Val && (pq4Val!=="Other"||pq4Other.trim());

  const logoutWhatsapp=()=>requestWhatsappLogout();const sendWhatsApp=(id)=>{setSendingWA(p=>({...p,[id]:true}));requestWhatsappSend(id).then(()=>setTimeout(()=>setSendingWA(p=>({...p,[id]:false})),3000)).catch(()=>setSendingWA(p=>({...p,[id]:false})));};const emailValid=(e)=>/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const ccOtherOk = (cc,custom)=>cc!=="+other" || /^\+?\d{1,5}$/.test((custom||"").trim());
  const infoOk = info.studentName && emailValid(info.email) && validatePhone(info.phone, info.studentCC) && ccOtherOk(info.studentCC,info.studentCCCustom) && info.school && info.gender &&
                 emailValid(info.parentEmail) && validatePhone(info.parentPhone, info.parentCC) && ccOtherOk(info.parentCC,info.parentCCCustom) &&
                 effCountry && info.city && effGrade && info.referral;

  const scoredPqs  = PARENT_QS.filter(q=>q.id!=="pq4");
  const parentOk   = scoredPqs.every(q=>parentAns[q.id]) && pq4Ok;

  /* ── Re-send any lead that failed to save on an earlier visit (DK 8 Oct 2026) ── */
  useEffect(()=>{ flushPendingLeads().catch(()=>{}); },[]);

  /* ── Browser tab title ── */
  useEffect(()=>{
    const admin = ["admin","adminDetail","adminLogin","partners"].includes(view);
    document.title = admin ? "NM Squad — Admin" : "NM Squad First Meeting Form";
  },[view]);

  /* ── Firebase real-time listener (admin only) ── */
  useEffect(()=>{
    if (view==="admin"||view==="adminDetail") {
      if (!unsubRef.current) {
        unsubRef.current = subscribeLeads(freshLeads => setLeads(freshLeads));
      }
      const unsubWa = subscribeWhatsappSettings(s => setWaSettings(s));
      return () => unsubWa();
    } else {
      if (unsubRef.current) { unsubRef.current(); unsubRef.current=null; }
    }
    return ()=>{};
  },[view]);

  /* ── URL ↔ view sync ──
     - When `view` enters an admin section we push the matching path so
       the URL is bookmarkable and refresh re-opens the same screen.
     - When the browser fires popstate (back/forward) we re-derive view
       from the URL so navigation feels native. */
  useEffect(() => {
    const want = ADMIN_VIEW_TO_PATH[view];
    if (want) {
      if (window.location.pathname !== want) {
        window.history.pushState({}, "", want);
      }
    } else if (view === "welcome" &&
        (parseAdminPath(window.location.pathname))) {
      // Logout / back-to-public — clear the admin URL.
      window.history.pushState({}, "", "/");
    }
  }, [view]);
  useEffect(() => {
    function onPop() {
      const want = parseAdminPath(window.location.pathname);
      if (want) {
        const authed = localStorage.getItem("nmAdminAuth") === "1";
        setView(authed ? want : "adminLogin");
      } else {
        setView("welcome");
      }
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /* ── Admin login ── */
  const doAdminLogin = () => {
    if (adU===ADMIN_U && adP===ADMIN_P) {
      localStorage.setItem("nmAdminAuth", "1");
      setAdErr("");
      adminSignIn().catch(()=>{});
      // After login, jump to whatever admin section the URL pointed at —
      // so /admin/tuap → tuap, /whatsapp → communications, etc.
      setView(parseAdminPath(window.location.pathname) || "admin");
    }
    else setAdErr("Incorrect credentials.");
  };

  /* ── Generate report ── */
  const generate = async () => {
    setView("generating");

    const token      = "NM" + Date.now().toString().slice(-6);
    const scores     = calculateScores(interests);
    const leadScores = calculateGuidance(parentAns);
    const cls        = classifyLead(leadScores.conversionScore);

    const fullInfo = {
      ...info,
      country:         effCountry,
      grade:           effGrade,
      gender:          effGender,
      leadPerson:      pq4Val,
      leadPersonOther: pq4Val==="Other" ? pq4Other : "",
      phone:       fullPhone(info.studentCC, info.phone, info.studentCCCustom),
      parentPhone: fullPhone(info.parentCC, info.parentPhone, info.parentCCCustom),
    };

    const sub = {
      token, info:fullInfo, scores, leadScores,
      interests, parentAns, classification:cls,
      aiInsights:null, submittedAt:new Date().toISOString(),
    };

    /* DK 8 Oct 2026: SAVE THE LEAD FIRST (before AI, report and emails), with retries and an offline copy.
       NM557139 was emailed to the family but never saved because the save used to run last. */
    let leadId = null;
    try {
      const docRef = await saveLeadReliable(sub);
      leadId = docRef.id; sub.firebaseId = leadId;
    } catch(e) {
      console.warn("Lead save failed, kept locally for retry:", e.message);
    }

    /* AI with hard timeout */
    try {
      const ai = await Promise.race([
        generateAI(sub),
        new Promise((_,rej)=>setTimeout(()=>rej(new Error("timeout")),25000)),
      ]);
      sub.aiInsights = ai;
    } catch(e) {
      console.warn("AI skipped:", e.message);
    }

    /* Show report immediately */
    setReport(sub);
    setView("report");

    /* Auto-send email to student and parent immediately after report shows */
    setTimeout(() => {
      sendStudentReportEmail(fullInfo, scores, leadScores, token);
      // Also send to parent email if different
      if (fullInfo.parentEmail && fullInfo.parentEmail !== fullInfo.email) {
        sendStudentReportEmail({...fullInfo, email: fullInfo.parentEmail}, scores, leadScores, token);
      }
    }, 500);

    /* Lead is already saved above; attach the AI insights to it */
    try {
      if (sub.aiInsights && leadId) await saveAIInsights(leadId, sub.aiInsights);
    } catch(e) {
      console.warn("AI insights save failed:", e.message);
    }

    /* ── Notify the NM Squad team of the new lead, after Firebase save.
       The student and parent receive the report email separately above. ── */
    try {
      const r = await sendMail({
        to: "mandhana.office@gmail.com",
        subject: "New FME lead: " + fullInfo.studentName + " (" + token + ")",
        html: buildLeadNotificationEmail(fullInfo, scores, leadScores, cls, token),
      });
      console.log("Lead notification email:", r.ok ? "sent" : "failed", r.error || "");
    } catch(e) {
      console.error("Lead notification email failed:", e);
    }
  };

  /* ── Status + comment updates ── */
  const updStatus = async(id, v) => {
    setLeads(prev=>prev.map(l=>l.firebaseId===id?{...l,status:v}:l));
    await updateLeadStatus(id, v);
  };

  const updComment = async(id, v) => {
    setLeads(prev=>prev.map(l=>l.firebaseId===id?{...l,comments:v}:l));
    await updateLeadComments(id, v);
  };

  const delLead = async(fid) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    setLeads(prev=>prev.filter(l=>l.firebaseId!==fid));
    try { await deleteLead(fid); } catch(e) { console.warn(e); }
  };

  // Rajib's handover: turn an FM lead into a TUAP student. This now fires the
  // whole lifecycle — WhatsApp group + welcome + group pic + onboarding email
  // all kick off automatically.
  const convertToTUAP = async(lead) => {
    if (lead.convertedToTuap) { setView("tuap"); return; }
    if (!window.confirm(
      `Convert ${lead.studentName||"this lead"} to a TUAP student?\n\n` +
      `This will automatically:\n` +
      `• Create their student record + portal\n` +
      `• Create their WhatsApp group (welcome message + group picture)\n` +
      `• Email them the onboarding link\n\n` +
      `Continue?`)) return;
    const nttDone = window.confirm(
      "Was the NTT done for this student?\n\nOK = Yes, NTT was done\nCancel = No, NTT was not done");
    try {
      // convertLeadToTuap already posts the NM Squad group alert tagging Ravisha
      // (tuap.js), so no separate message is needed here.
      await convertLeadToTuap(lead, nttDone);
      setLeads(prev=>prev.map(l=>l.firebaseId===lead.firebaseId?{...l,convertedToTuap:true}:l));
      setView("tuap");
    } catch(e) { alert("Conversion failed: "+(e.message||e)); }
  };

  // "Register for NTT": email a completed lead their secure online-test link
  // (student + parent, cc office). Idempotent — re-clicking re-sends the link.
  const registerNTT = async(lead) => {
    const fid = lead.firebaseId;
    const resend = ["registered","in_progress","completed"].includes(lead.nttStatus);
    if (!window.confirm(
      `${resend ? "Re-send" : "Send"} the Natural Talent Test link to ${lead.studentName||"this student"}?\n\n` +
      `We'll email ${lead.email||"the student"}` +
      `${lead.parentEmail ? " and "+lead.parentEmail : ""} (cc office) a unique secure link to take the NTT online.`)) return;
    setNttBusy(p=>({...p,[fid]:true}));
    try {
      const res = await registerForNTT(lead);
      if (res.ok) {
        setLeads(prev=>prev.map(l=>l.firebaseId===fid && !["in_progress","completed"].includes(l.nttStatus)
          ? {...l,nttStatus:"registered"} : l));
        alert("NTT link emailed to "+(lead.email||"the student")+(lead.parentEmail?" and "+lead.parentEmail:"")+".");
      } else {
        alert("Could not send the NTT link: "+(res.error||"unknown error"));
      }
    } catch(e) { alert("Could not send the NTT link: "+(e.message||e)); }
    finally { setNttBusy(p=>({...p,[fid]:false})); }
  };

  /* ── PDF ── */

  const sendStudentReportEmail = async(ri, sc, ld, token) => {
    setEmailSending(true);
    const waLink = 'https://wa.me/917439012505?text=' + encodeURIComponent('Dear Rajib, I have just completed the inquiry form for the first meeting with Dr. Neeraj Mandhana. My token number is ' + token + '. I look forward to connecting with you and scheduling the session.');
    const topI = (sc.topInterests||[]).slice(0,8);
    const interestTags = topI.map(t => '<span style="padding:5px 12px;background:#fffbeb;border:1px solid #F5C518;border-radius:20px;font-size:12px;color:#7A5F00;font-weight:600;">' + t + '</span>').join(' ');
    const html = '<!DOCTYPE html><html><head><meta charset="utf-8"/></head><body style="margin:0;padding:20px;font-family:Arial,sans-serif;background:#f8f9fa;"><div style="max-width:620px;margin:0 auto;background:#fff;border-radius:14px;overflow:hidden;border:1px solid #e5e7eb;"><div style="background:#F5C518;padding:22px 28px;"><div style="font-size:11px;color:#7A5F00;letter-spacing:0.1em;text-transform:uppercase;">NM Squad</div><div style="font-size:20px;font-weight:900;color:#1a1a1a;">First Meeting Enquiry</div><div style="font-size:10px;color:#7A5F00;margin-top:4px;">Token: ' + token + '</div></div><div style="padding:20px 28px;border-bottom:1px solid #f3f4f6;"><table style="width:100%;border-collapse:collapse;"><tr><td style="padding:4px 8px 4px 0;width:25%"><div style="font-size:10px;color:#9ca3af;text-transform:uppercase;">Student</div><div style="font-size:14px;font-weight:700;color:#111;">' + ri.studentName + '</div></td><td style="padding:4px 8px;width:25%"><div style="font-size:10px;color:#9ca3af;text-transform:uppercase;">School</div><div style="font-size:14px;font-weight:700;color:#111;">' + (ri.school||'—') + '</div></td><td style="padding:4px 8px;width:25%"><div style="font-size:10px;color:#9ca3af;text-transform:uppercase;">Grade</div><div style="font-size:14px;font-weight:700;color:#111;">' + ri.grade + '</div></td><td style="padding:4px 0 4px 8px;width:25%"><div style="font-size:10px;color:#9ca3af;text-transform:uppercase;">Country</div><div style="font-size:14px;font-weight:700;color:#111;">' + ri.country + '</div></td></tr></table></div><div style="padding:24px 28px;border-bottom:1px solid #f3f4f6;"><div style="font-size:15px;font-weight:800;color:#111;margin-bottom:18px;">Our Initial Assessment</div><div style="margin-bottom:16px;"><div style="display:flex;justify-content:space-between;margin-bottom:7px;"><span style="font-size:14px;font-weight:700;color:#2563eb;">Data, Logic, Machines</span><span style="font-size:18px;font-weight:900;color:#2563eb;">' + sc.logicPct + '%</span></div><div style="height:20px;background:#eff6ff;border-radius:20px;overflow:hidden;"><div style="height:100%;width:' + sc.logicPct + '%;background:#2563eb;border-radius:20px;"></div></div><div style="font-size:11px;color:#9ca3af;margin-top:4px;">IQ — Analytical thinking, data and systems</div></div><div style="margin-bottom:16px;"><div style="display:flex;justify-content:space-between;margin-bottom:7px;"><span style="font-size:14px;font-weight:700;color:#ea580c;">People, Communication, Emotion</span><span style="font-size:18px;font-weight:900;color:#ea580c;">' + sc.peoplePct + '%</span></div><div style="height:20px;background:#fff7ed;border-radius:20px;overflow:hidden;"><div style="height:100%;width:' + sc.peoplePct + '%;background:#ea580c;border-radius:20px;"></div></div><div style="font-size:11px;color:#9ca3af;margin-top:4px;">EQ + DQ — Social intelligence and creative expression</div></div>' + (topI.length > 0 ? '<div style="margin-top:18px;padding:14px;background:#fffde7;"><div style="font-size:11px;font-weight:700;color:#7A5F00;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:10px;">Things Most Interested In</div><div style="line-height:2;">' + interestTags + '</div></div>' : '') + '</div><div style="padding:20px 28px;border-bottom:1px solid #f3f4f6;"><div style="font-size:14px;font-weight:800;color:#111;margin-bottom:16px;">Guidance Preference</div><div style="margin-bottom:14px;"><div style="font-size:13px;font-weight:700;color:#D4A017;margin-bottom:6px;">Personal Mentorship ' + ld.personalScore + '%</div><div style="background:#f5f5f5;height:16px;border-radius:20px;overflow:hidden;"><div style="height:100%;width:' + ld.personalScore + '%;background:#F5C518;border-radius:20px;"></div></div></div><div><div style="font-size:13px;font-weight:700;color:#374151;margin-bottom:6px;">Professional Outcomes ' + ld.professionalScore + '%</div><div style="background:#f5f5f5;height:16px;border-radius:20px;overflow:hidden;"><div style="height:100%;width:' + ld.professionalScore + '%;background:#6b7280;border-radius:20px;"></div></div></div></div><div style="padding:22px 28px;background:#F5C518;"><div style="font-size:15px;font-weight:800;color:#111111;margin-bottom:10px;">Next Steps</div><p style="margin:0 0 14px;font-size:14px;color:#111111;line-height:1.7;">Thank you for filling this. Rajib from our team will now connect with you. Please feel free to chat with him here.</p><div style="font-size:14px;font-weight:700;color:#111;margin-bottom:6px;">Rajib B</div><div style="font-size:13px;color:#374151;margin-bottom:4px;">mandhana.office@gmail.com</div><div style="font-size:13px;color:#374151;margin-bottom:16px;">+91 74390 12505</div><a href="' + waLink + '" style="display:block;text-align:center;padding:13px;background:#25D366;border-radius:8px;color:#fff;font-size:14px;font-weight:700;text-decoration:none;">WhatsApp NM Squad</a><div style="margin-top:12px;text-align:center;font-size:12px;color:#111111;">Quote token: ' + token + '</div></div></div></body></html>';
    const r = await sendMail({
      to: ri.email,
      subject: 'Your First Meeting Enquiry — NM Squad (Token ' + token + ')',
      html,
    });
    if (r.ok) {
      console.log('Student report email sent to', ri.email);
      alert('Report emailed to ' + ri.email + ' successfully!');
    } else {
      console.error('Student report email failed:', r.error);
      alert('Email failed: ' + (r.error || 'Check console'));
    }
    setEmailSending(false);
  };

  const handlePdf = async(elId, fname) => {
    setPdfL(true);
    await generatePDF(elId, fname);
    setPdfL(false);
  };

  /* ── Reset ── */
  const resetAll = () => {
    setReport(null); setInterests({}); setParentAns({}); setPq4Other(""); setPage(0);
    setInfo({studentName:"",email:"",phone:"",school:"",parentEmail:"",parentPhone:"",studentCC:"+91",parentCC:"+91",studentCCCustom:"",parentCCCustom:"",country:"",countryOther:"",city:"",grade:"",gradeOther:"",referral:""});
    setView("welcome");
  };

  /* ════════════════════════════════════════════
     VIEWS
  ════════════════════════════════════════════ */

  /* WELCOME */
  if (view==="welcome") return (
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#fffbeb 0%,#fff 65%)",display:"flex",alignItems:"center",justifyContent:"center",padding:mob?20:32,fontFamily:"sans-serif"}}>
      <style>{PCSS}</style>
      <div style={{maxWidth:520,width:"100%",textAlign:"center"}}>
        <div style={{marginBottom:20}}><NMLogo size={80}/></div>
        <p style={{margin:"0 0 8px",fontSize:12,letterSpacing:".12em",textTransform:"uppercase",color:SUB}}>Neeraj Mandhana</p>
        <h1 style={{margin:"0 0 14px",fontSize:mob?26:34,fontWeight:800,color:BLACK,lineHeight:1.2}}>First Meeting Enquiry</h1>
        <p style={{margin:"0 0 28px",fontSize:16,color:MID,lineHeight:1.8}}>Please take a moment to complete this brief survey so we can better understand your child and schedule an appointment at the earliest available slot or as per the slot availability.</p>
        <Btn onClick={()=>setView("info")} full>Next Step →</Btn>
        <button onClick={()=>setView("adminLogin")} style={{marginTop:22,fontSize:12,color:"#d1d5db",background:"none",border:"none",cursor:"pointer"}}>Admin</button>
      </div>
    </div>
  );

  /* INFO */
  if (view==="info") return (
    <div style={{minHeight:"100vh",background:BG,padding:mob?20:32,fontFamily:"sans-serif"}}>
      <style>{PCSS}</style>
      <div style={{maxWidth:580,margin:"0 auto"}}>
        <button onClick={()=>setView("welcome")} style={{fontSize:14,color:MID,background:"none",border:"none",cursor:"pointer",marginBottom:18}}>← Back</button>
        <h2 style={{margin:"0 0 4px",fontSize:26,fontWeight:800,color:BLACK}}>About the student</h2>
        <p style={{margin:"0 0 22px",fontSize:14,color:MID}}>All fields are required.</p>
        <Card>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Student Name *</label>
            <input type="text" placeholder="" value={info.studentName} onChange={e=>setInfo(p=>({...p,studentName:e.target.value}))} style={INP}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:14,marginBottom:16}}>
            <div>
              <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Student Email *</label>
              <input type="email" placeholder="" value={info.email} onChange={e=>setInfo(p=>({...p,email:e.target.value}))} style={INP}/>{info.email&&!emailValid(info.email)&&<div style={{fontSize:11,color:"#dc2626",marginTop:4,fontWeight:600}}>Enter a valid email address</div>}
            </div>
            <div>
              <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Student Phone *</label>
              <div style={{display:"flex",gap:8}}>
                <select value={info.studentCC} onChange={e=>setInfo(p=>({...p,studentCC:e.target.value}))}
                  style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:8,padding:"12px 8px",fontSize:13,outline:"none",flexShrink:0,width:"118px",cursor:"pointer"}}>
                  {COUNTRY_CODES.map(cc=><option key={cc.flag+cc.code} value={cc.code}>{cc.code==="+other"?"Other 🌐":cc.name+" "+flagEmoji(cc.flag)+" "+cc.code}</option>)}
                </select>
                <div style={{flex:1}}>
                  <input type="tel" value={info.phone}
                    onChange={e=>setInfo(p=>({...p,phone:e.target.value.replace(/[^0-9]/g,'')}))}
                    onBlur={()=>setSpTouched(true)}
                    maxLength={15} style={{width:"100%",boxSizing:"border-box",background:"#f9fafb",color:"#111",border:spTouched&&phoneHint(info.phone,info.studentCC)?"2px solid #dc2626":"1px solid #e5e7eb",borderRadius:8,padding:"12px 14px",fontSize:15,outline:"none",fontFamily:"sans-serif"}}/>
                  {spTouched&&phoneHint(info.phone,info.studentCC)&&(
                    <div style={{fontSize:12,color:"#dc2626",marginTop:5,fontWeight:600,background:"#fef2f2",padding:"6px 10px",borderRadius:6,border:"1px solid #fecaca"}}>
                      ⚠️ {phoneHint(info.phone,info.studentCC)}
                    </div>
                  )}
                </div>
              </div>
              {info.studentCC==="+other"&&(
                <input type="text" placeholder="Enter country code e.g. +672" value={info.studentCCCustom}
                  onChange={e=>setInfo(p=>({...p,studentCCCustom:e.target.value.replace(/[^0-9+]/g,'')}))}
                  maxLength={6} style={{...INP,marginTop:8}}/>
              )}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:14,marginBottom:16}}>
            <div>
              <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Parent Email *</label>
              <input type="email" placeholder="" value={info.parentEmail} onChange={e=>setInfo(p=>({...p,parentEmail:e.target.value}))} style={INP}/>{info.parentEmail&&!emailValid(info.parentEmail)&&<div style={{fontSize:11,color:"#dc2626",marginTop:4,fontWeight:600}}>Enter a valid email address</div>}
            </div>
            <div>
              <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Parent Phone *</label>
              <div style={{display:"flex",gap:8}}>
                <select value={info.parentCC} onChange={e=>setInfo(p=>({...p,parentCC:e.target.value}))}
                  style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:8,padding:"12px 8px",fontSize:13,outline:"none",flexShrink:0,width:"118px",cursor:"pointer"}}>
                  {COUNTRY_CODES.map(cc=><option key={cc.flag+cc.code} value={cc.code}>{cc.code==="+other"?"Other 🌐":cc.name+" "+flagEmoji(cc.flag)+" "+cc.code}</option>)}
                </select>
                <div style={{flex:1}}>
                  <input type="tel" value={info.parentPhone}
                    onChange={e=>setInfo(p=>({...p,parentPhone:e.target.value.replace(/[^0-9]/g,'')}))}
                    onBlur={()=>setPpTouched(true)}
                    maxLength={15} style={{width:"100%",boxSizing:"border-box",background:"#f9fafb",color:"#111",border:ppTouched&&phoneHint(info.parentPhone,info.parentCC)?"2px solid #dc2626":"1px solid #e5e7eb",borderRadius:8,padding:"12px 14px",fontSize:15,outline:"none",fontFamily:"sans-serif"}}/>
                  {ppTouched&&phoneHint(info.parentPhone,info.parentCC)&&(
                    <div style={{fontSize:12,color:"#dc2626",marginTop:5,fontWeight:600,background:"#fef2f2",padding:"6px 10px",borderRadius:6,border:"1px solid #fecaca"}}>
                      ⚠️ {phoneHint(info.parentPhone,info.parentCC)}
                    </div>
                  )}
                </div>
              </div>
              {info.parentCC==="+other"&&(
                <input type="text" placeholder="Enter country code e.g. +672" value={info.parentCCCustom}
                  onChange={e=>setInfo(p=>({...p,parentCCCustom:e.target.value.replace(/[^0-9+]/g,'')}))}
                  maxLength={6} style={{...INP,marginTop:8}}/>
              )}
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>School *</label>
            <input type="text" placeholder="" value={info.school} onChange={e=>setInfo(p=>({...p,school:e.target.value}))} style={INP}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:14,marginBottom:16}}>
            <div>
              <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Country *</label>
              <select value={info.country} onChange={e=>setInfo(p=>({...p,country:e.target.value}))} style={SEL}>
                <option value="">Select country</option>
                {COUNTRIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              {info.country==="Other 🌍"&&(
                <input type="text" placeholder="" value={info.countryOther} onChange={e=>setInfo(p=>({...p,countryOther:e.target.value}))} style={{...INP,marginTop:8}}/>
              )}
            </div>
            <div>
              <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>City *</label>
              <input type="text" placeholder="" value={info.city} onChange={e=>setInfo(p=>({...p,city:e.target.value}))} style={INP}/>
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Grade *</label>
            <select value={info.grade} onChange={e=>setInfo(p=>({...p,grade:e.target.value}))} style={SEL}>
              <option value="">Select grade</option>
              {GRADES.map(g=><option key={g} value={g}>{g}</option>)}
            </select>
            {info.grade==="Other"&&(
              <input type="text" placeholder="" value={info.gradeOther} onChange={e=>setInfo(p=>({...p,gradeOther:e.target.value}))} style={{...INP,marginTop:8}}/>
            )}
          </div>
          <div style={{marginBottom:18}}>
            <label style={{display:"block",fontWeight:700,marginBottom:7}}>Gender *</label>
            <div style={{display:"flex",gap:24,alignItems:"center",flexWrap:"wrap",marginBottom:4}}>
              {["Male","Female","Other"].map(g=>(
                <label key={g} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontWeight:400}}>
                  <input type="radio" name="gender" value={g} checked={info.gender===g} onChange={()=>setInfo(p=>({...p,gender:g,genderOther:""}))} style={{accentColor:"#d4a017",width:16,height:16}} />
                  {g}
                </label>
              ))}
            </div>
            {info.gender==="Other" && (
              <input placeholder="Please specify" value={info.genderOther||""} onChange={e=>setInfo(p=>({...p,genderOther:e.target.value}))} style={{...INP,marginTop:8,maxWidth:260}} />
            )}
          </div>
          <div style={{marginBottom:22}}>
            <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>May we know who referred you to us?</label>
            <input type="text" placeholder="" value={info.referral} onChange={e=>setInfo(p=>({...p,referral:e.target.value}))} style={INP}/>
          </div>
          <Btn onClick={()=>setView("interests")} disabled={!infoOk} full>Next Step →</Btn>
        </Card>
      </div>
    </div>
  );

  /* INTERESTS */
  if (view==="interests") return (
    <div style={{minHeight:"100vh",background:BG,padding:mob?14:28,fontFamily:"sans-serif"}}>
      <div style={{maxWidth:720,margin:"0 auto"}}>
        <Steps cur={cur}/>
        <h2 style={{margin:"0 0 4px",fontSize:mob?20:24,fontWeight:800,color:BLACK}}>Interest Survey</h2>
        <p style={{margin:"0 0 16px",fontSize:15,color:MID,lineHeight:1.7}}>Please take a few minutes to complete the questions below so we can better understand your interests and strengths. We recommend that the student fills this out personally for the most accurate insights.</p>
        <Prog current={answeredCnt} total={INTEREST_MAP.length} label="Activities answered"/>
        <div style={{display:"flex",gap:4,marginBottom:14}}>
          {Array.from({length:totalPages}).map((_,i)=>(
            <div key={i} onClick={()=>setPage(i)} style={{height:5,flex:1,background:i<=page?Y:"#e5e7eb",borderRadius:9999,cursor:"pointer"}}/>
          ))}
        </div>
        <Card style={{padding:0,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:`1fr repeat(3,${mob?"58px":"88px"})`,padding:mob?"9px 12px 7px":"9px 20px 7px",borderBottom:`1px solid ${BORDER}`,background:"#f9fafb"}}>
            <div style={{fontSize:12,color:MID,fontWeight:600}}>Activity</div>
            {[["Interested","#15803d"],["Maybe","#ca8a04"],["Not","#dc2626"]].map(([l,c])=>(
              <div key={l} style={{textAlign:"center",fontSize:11,color:c,fontWeight:700}}>{l}</div>
            ))}
          </div>
          {pageItems.map((item,idx)=>{
            const sel=interests[item.label];
            return (
              <div key={item.label} style={{display:"grid",gridTemplateColumns:`1fr repeat(3,${mob?"58px":"88px"})`,alignItems:"center",padding:mob?"11px 12px":"12px 20px",borderBottom:idx<pageItems.length-1?"1px solid #fafafa":"none",background:sel?"#fffbeb":"#fff"}}>
                <span style={{fontSize:mob?13:14,color:BLACK,fontWeight:500,paddingRight:6}}>{item.label}</span>
                {["interested","maybe","not"].map((val,vi)=>{
                  const active=sel===val;
                  const cols=["#15803d","#ca8a04","#dc2626"];
                  return (
                    <div key={val} style={{display:"flex",justifyContent:"center"}}>
                      <div onClick={()=>setInterests(p=>({...p,[item.label]:val}))} style={{width:mob?24:28,height:mob?24:28,borderRadius:"50%",border:`2.5px solid ${active?cols[vi]:BORDER}`,background:active?cols[vi]:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {active&&<div style={{width:mob?7:9,height:mob?7:9,borderRadius:"50%",background:"#fff"}}/>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Card>
        {(() => {
          const pageAnswered = pageItems.every(item => interests[item.label]);
          return (
            <div style={{display:"flex",gap:10,justifyContent:"space-between",alignItems:"center"}}>
              <Btn onClick={()=>page>0?setPage(p=>p-1):setView("info")} color="outline">← Back</Btn>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                {!pageAnswered&&<span style={{fontSize:12,color:"#dc2626",fontWeight:600}}>Please answer all activities on this page</span>}
                {page<totalPages-1
                  ?<Btn onClick={()=>setPage(p=>p+1)} disabled={!pageAnswered}>Next ({page+1}/{totalPages}) →</Btn>
                  :<Btn onClick={()=>setView("parentQ")} disabled={!pageAnswered}>Next Step →</Btn>}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );

  /* PARENT Q */
  if (view==="parentQ") return (
    <div style={{minHeight:"100vh",background:BG,padding:mob?14:28,fontFamily:"sans-serif"}}>
      <div style={{maxWidth:720,margin:"0 auto"}}>
        <Steps cur={cur}/>
        <h2 style={{margin:"0 0 4px",fontSize:mob?20:24,fontWeight:800,color:BLACK}}>A few questions for parents</h2>
        <p style={{margin:"0 0 16px",fontSize:15,color:MID}}>Help us understand your expectations and goals for your child's educational journey with NM Squad.</p>
        <Prog current={Object.keys(parentAns).length} total={PARENT_QS.length} label="Answered"/>
        {PARENT_QS.map((pq,idx)=>(
          <Card key={pq.id} style={{padding:"18px 20px"}}>
            <p style={{margin:"0 0 12px",fontSize:mob?14:15,color:BLACK,lineHeight:1.7,fontWeight:600}}>
              <span style={{color:"#d1d5db",marginRight:8,fontWeight:400}}>{idx+1}.</span>{pq.q}
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:9}}>
              {pq.opts.map((opt,oi)=>{
                const active=parentAns[pq.id]===opt.text;
                return (
                  <button key={oi} onClick={()=>setParentAns(p=>({...p,[pq.id]:opt.text}))}
                    style={{textAlign:"left",padding:"12px 16px",fontSize:14,border:active?`2px solid ${Y}`:`1px solid ${BORDER}`,background:active?Y:"#f9fafb",borderRadius:8,cursor:"pointer",color:active?YD:BLACK,fontWeight:active?600:400,lineHeight:1.5,fontFamily:"sans-serif"}}>
                    {opt.text}
                  </button>
                );
              })}
              {pq.id==="pq4"&&parentAns.pq4==="Other"&&(
                <input type="text" placeholder="" value={pq4Other} onChange={e=>setPq4Other(e.target.value)} style={{...INP,marginTop:4}}/>
              )}
            </div>
          </Card>
        ))}
        <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
          <Btn onClick={()=>setView("interests")} color="outline">← Back</Btn>
          <Btn onClick={generate} disabled={!parentOk}>Next Step →</Btn>
        </div>
      </div>
    </div>
  );

  /* GENERATING */
  if (view==="generating") return (
    <div style={{minHeight:"100vh",background:"#fff",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif",gap:24,padding:24}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <NMLogo size={70}/>
      <div style={{width:54,height:54,border:`4px solid ${BORDER}`,borderTopColor:Y,borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
      <div style={{textAlign:"center"}}>
        <h2 style={{margin:"0 0 8px",fontSize:22,fontWeight:800,color:BLACK}}>Generating your report</h2>
        <p style={{margin:0,fontSize:15,color:MID}}>Analysing your profile and generating personalised insights…</p>
        <p style={{margin:"8px 0 0",fontSize:13,color:SUB}}>This takes up to 25 seconds</p>
      </div>
    </div>
  );

  /* CLIENT REPORT */
  if (view==="report"&&report) {
    const {token,info:ri,scores:sc,leadScores:ld} = report;
    const waUrl  = `https://wa.me/917439012505?text=${encodeURIComponent(`Dear Rajib, I have just completed the inquiry form for the first meeting with Dr. Neeraj Mandhana. My token number is ${token}. I look forward to connecting with you and scheduling the session.`)}`;
    const mBody  = `Dear ${ri.studentName},\n\nToken: ${token}\n\nThank you for completing the First Meeting Enquiry. Rajib from our team will connect with you shortly.\n\nmandhana.office@gmail.com | +91 74390 12505\n\nNM Squad · ${new Date().toLocaleDateString()}`;
    const mailto = `mailto:${ri.email}?subject=${encodeURIComponent("First Meeting Enquiry — "+ri.studentName)}&body=${encodeURIComponent(mBody)}`;

    const scoreList = [
      {label:"IQ",sub:"Analytical",score:sc.IQ,color:"#2563eb",bg:"#eff6ff"},
      {label:"EQ",sub:"Emotional", score:sc.EQ,color:"#ea580c",bg:"#fff7ed"},
      {label:"DQ",sub:"Creative",  score:sc.DQ,color:"#16a34a",bg:"#f0fdf4"},
      {label:"AQ",sub:"Physical",  score:sc.AQ,color:"#7c3aed",bg:"#faf5ff"},
    ];
    const sorted   = [...scoreList].sort((a,b)=>b.score-a.score);
    const domScore = sorted[0];
    const secScore = sorted[1];

    return (
      <div style={{background:BG,minHeight:"100vh",fontFamily:"sans-serif"}}>
        <style>{PCSS}</style>

        {/* Action bar */}
        <div className="np" style={{background:"#fff",borderBottom:`1px solid ${BORDER}`,padding:mob?"10px 14px":"10px 28px",display:"flex",gap:8,justifyContent:"center",alignItems:"center",flexWrap:"wrap"}}>
          <Btn onClick={()=>handlePdf("nm-report",`FME_${ri.studentName.replace(/ /g,"_")}.pdf`)} color="yellow" size="sm">{pdfLoading?"Generating…":"Download PDF"}</Btn>
          <a href={waUrl} target="_blank" rel="noopener noreferrer" style={{padding:"9px 16px",background:"#25D366",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,textDecoration:"none",display:"flex",alignItems:"center",gap:6}}>{WA_ICON}WhatsApp NM Squad</a>
          <Btn onClick={resetAll} color="outline" size="sm"><a href="https://www.neerajmandhana.com" style={{color:"inherit",textDecoration:"none"}}>Homepage</a></Btn>
        </div>

        <div id="nm-report" style={{maxWidth:860,margin:"0 auto",padding:mob?14:28}}>

          {/* Header */}
          <Card style={{border:`2px solid ${Y}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:14}}>
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <NMLogo size={mob?44:58}/>
                <div>
                  <p style={{margin:0,fontSize:11,color:SUB,letterSpacing:".1em",textTransform:"uppercase"}}>NM Squad</p>
                  <h1 style={{margin:0,fontSize:mob?20:26,fontWeight:800,color:BLACK}}>First Meeting Enquiry</h1>
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <p style={{margin:0,fontSize:11,color:SUB,textTransform:"uppercase",letterSpacing:".08em"}}>Token</p>
                <p style={{margin:0,fontSize:mob?22:28,fontWeight:900,color:YD}}>{token}</p>
                <p style={{margin:"3px 0 0",fontSize:11,color:SUB}}>{new Date().toLocaleDateString()}</p>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:mob?"repeat(2,1fr)":"repeat(3,1fr)",gap:12,borderTop:`1px solid ${BORDER}`,paddingTop:16,marginTop:16}}>
              {[["Student",ri.studentName],["School",ri.school||"—"],["Grade",ri.grade],["Phone",ri.phone],["Email",ri.email],["Parent Email",ri.parentEmail||"—"],["Parent Phone",ri.parentPhone||"—"],["Country",ri.country],["City",ri.city],["Referral",ri.referral||"—"]].map(([k,v])=>(
                <div key={k}>
                  <p style={{margin:0,fontSize:10,color:SUB,textTransform:"uppercase"}}>{k}</p>
                  <p style={{margin:"2px 0 0",fontWeight:700,color:BLACK,wordBreak:"break-word",lineHeight:1.4,fontSize:mob?12:13}}>{v||"—"}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Scores + Pie */}
          <div style={{display:"grid",gridTemplateColumns:"1fr",gap:14,marginBottom:14}}>
            <Card style={{marginBottom:0}}>
              <h3 style={{margin:"0 0 16px",fontSize:15,fontWeight:800,color:BLACK}}>Our Initial Assessment</h3>
              {/* Logic group */}
              <div style={{marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
                  <span style={{fontSize:14,fontWeight:700,color:"#2563eb"}}>Data, Logic, Machines</span>
                  <span style={{fontSize:20,fontWeight:900,color:"#2563eb"}}>{sc.logicPct}%</span>
                </div>
                <div style={{height:22,background:"#eff6ff",borderRadius:9999,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${sc.logicPct}%`,background:"#2563eb",borderRadius:9999,transition:"width .5s"}}/>
                </div>
                <div style={{fontSize:11,color:SUB,marginTop:4}}>IQ — Analytical thinking, data &amp; systems</div>
              </div>
              {/* People group */}
              <div style={{marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
                  <span style={{fontSize:14,fontWeight:700,color:"#ea580c"}}>People, Communication, Emotion</span>
                  <span style={{fontSize:20,fontWeight:900,color:"#ea580c"}}>{sc.peoplePct}%</span>
                </div>
                <div style={{height:22,background:"#fff7ed",borderRadius:9999,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${sc.peoplePct}%`,background:"#ea580c",borderRadius:9999,transition:"width .5s"}}/>
                </div>
                <div style={{fontSize:11,color:SUB,marginTop:4}}>EQ + DQ — Social intelligence &amp; creative expression</div>
              </div>
              {/* Top interests */}
              {sc.topInterests&&sc.topInterests.length>0&&(
                <div style={{marginTop:18}}>
                  <div style={{fontSize:12,fontWeight:700,color:YD,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Things most interested in</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {sc.topInterests.map((t,i)=>(
                      <span key={i} style={{padding:"4px 10px",background:"#F5C518",border:"none",borderRadius:20,fontSize:11,color:YD,fontWeight:600,whiteSpace:"nowrap"}}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </Card>
            <Card style={{marginBottom:0}}>
              <h3 style={{margin:"0 0 16px",fontSize:15,fontWeight:800,color:BLACK}}>Guidance Preference</h3>
              <div style={{marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
                  <span style={{fontSize:14,fontWeight:700,color:"#F5C518"}}>Personal Mentorship</span>
                  <span style={{fontSize:20,fontWeight:900,color:YD}}>{ld.personalScore}%</span>
                </div>
                <div style={{height:22,background:"#fffbeb",borderRadius:9999,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${ld.personalScore}%`,background:Y,borderRadius:9999,transition:"width .5s"}}/>
                </div>
                <div style={{fontSize:11,color:SUB,marginTop:4}}>Values relationship, trust and personal guidance</div>
              </div>
              <div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
                  <span style={{fontSize:14,fontWeight:700,color:MID}}>Professional Outcomes</span>
                  <span style={{fontSize:20,fontWeight:900,color:MID}}>{ld.professionalScore}%</span>
                </div>
                <div style={{height:22,background:"#f3f4f6",borderRadius:9999,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${ld.professionalScore}%`,background:MID,borderRadius:9999,transition:"width .5s"}}/>
                </div>
                <div style={{fontSize:11,color:SUB,marginTop:4}}>Values structured systems, prestige and outcomes</div>
              </div>
            </Card>
          </div>

          {/* About NM + Next Steps */}
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"3fr 2fr",gap:14}}>
            <Card style={{marginBottom:0,background:"#000000",padding:0,overflow:"hidden",display:"flex",alignItems:"stretch"}}>
              <img src="/about-nm.png" alt="About Neeraj Mandhana"
                style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top left",display:"block"}}
                onError={e=>{e.target.style.display="none";}}/>
            </Card>
            <Card style={{marginBottom:0,background:"#F5C518",border:"none"}}>
              <h3 style={{margin:"0 0 12px",fontSize:16,fontWeight:800,color:BLACK}}>Next Steps</h3>
              <p style={{margin:"0 0 14px",fontSize:14,color:BLACK,lineHeight:1.7}}>
                Thank you for filling this. Rajib from our team will now connect with you. Please feel free to chat with him here. Rajib will help you find the earliest possible appointment.
              </p>
              <div style={{fontSize:15,fontWeight:800,color:BLACK,marginBottom:8}}>Rajib B</div>
              <div style={{fontSize:14,color:MID,marginBottom:5}}>📧 mandhana.office@gmail.com</div>
              <div style={{fontSize:14,color:MID,marginBottom:18}}>📞 +91 74390 12505</div>
              <a href={waUrl} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"13px",background:"#25D366",borderRadius:8,color:"#fff",fontSize:14,fontWeight:700,textDecoration:"none"}}>
                {WA_ICON}WhatsApp NM Squad
              </a>
              <div style={{marginTop:12,textAlign:"center",fontSize:12,color:BLACK}}>Quote token: <strong style={{color:BLACK}}>{token}</strong></div>
            </Card>
          </div>

          <p style={{textAlign:"center",fontSize:11,color:"#d1d5db",padding:"12px 0 8px"}}>NM Squad · First Meeting Enquiry · {new Date().toLocaleDateString()} · Token: {token}</p>
        </div>
      </div>
    );
  }

  /* ADMIN LOGIN */
  if (view==="adminLogin") return (
    <div style={{minHeight:"100vh",background:BG,display:"flex",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"sans-serif"}}>
      <div style={{maxWidth:400,width:"100%"}}>
        <div style={{textAlign:"center",marginBottom:22}}>
          <NMLogo size={68}/>
          <h2 style={{margin:"14px 0 4px",fontSize:24,fontWeight:800,color:BLACK}}>NM Squad Admin</h2>
          <p style={{margin:0,fontSize:14,color:MID}}>Internal access only</p>
        </div>
        <Card>
          {adErr&&<div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:6,padding:"10px 14px",marginBottom:14,fontSize:14,color:"#dc2626",fontWeight:600}}>{adErr}</div>}
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Username</label>
            <input type="text" value={adU} onChange={e=>setAdU(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doAdminLogin()} style={INP}/>
          </div>
          <div style={{marginBottom:20}}>
            <label style={{display:"block",fontSize:14,fontWeight:700,color:BLACK,marginBottom:7}}>Password</label>
            <input type="password" value={adP} onChange={e=>setAdP(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doAdminLogin()} style={INP}/>
          </div>
          <Btn onClick={doAdminLogin} full>Login →</Btn>
        </Card>
        <div style={{textAlign:"center"}}>
          <button onClick={()=>setView("welcome")} style={{fontSize:14,color:MID,background:"none",border:"none",cursor:"pointer"}}>← Back</button>
        </div>
      </div>
    </div>
  );

  /* TRUSTED PARTNERS */
  if (view==="partners") {
    return <TrustedPartners onBack={()=>setView("admin")} leads={leads}/>;
  }

  /* TUAP STUDENTS */
  if (view==="tuap") {
    return <TuapAdmin onBack={()=>setView("admin")}/>;
  }

  /* COMMUNICATIONS — Ravisha's hub, deep-linked at /admin/communications,
     /whatsapp or /communication. Loads its own students subscription so it
     can stand alone without the TUAP admin around it. */
  if (view==="communications") {
    return <RavishaHubStandalone onBack={()=>setView("admin")} />;
  }

  /* ADMIN DASHBOARD */
  if (view==="admin") {
    const inbox   = leads.filter(l=>!DONE_S.includes(l.status||"Fresh Enquiry"));
    const done    = leads.filter(l=> DONE_S.includes(l.status||"Fresh Enquiry"));
    const base    = adTab==="inbox"?inbox:done;
    const q       = searchQ.trim().toLowerCase();
    const display = !q ? base : base.filter(l => {
      const hay = [
        l.token, l.studentName, l.email, l.phone, l.school, l.city, l.country,
        l.parentEmail, l.parentPhone, l.referral, l.leadPerson,
        l.status, l.grade, l.gender, l.comments
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });

    // Visible columns (in order). Everything else lives in the full report.
    const COLS = [
      {key:"wa",        label:"WhatsApp",     val:l=> l.whatsappSent===true?2 : l.whatsappSent===false?0 : 1},
      {key:"date",      label:"Date & Time",  val:l=> l.createdAt?l.createdAt.seconds:0},
      {key:"name",      label:"Name",         val:l=>(l.studentName||"").toLowerCase()},
      {key:"country",   label:"Country",      val:l=>(l.country||"").toLowerCase()},
      {key:"gender",    label:"Gender",       val:l=>(l.gender||"").toLowerCase()},
      {key:"school",    label:"School",       val:l=>(l.school||"").toLowerCase()},
      {key:"reference", label:"Reference",    val:l=>(l.referral||"").toLowerCase()},
      {key:"status",    label:"Status",       val:l=>(l.status||"").toLowerCase()},
      {key:"notes",     label:"Notes",        val:l=>(l.comments||"").toLowerCase()},
    ];
    const sortCol = COLS.find(c=>c.key===sort.key);
    const sorted = sortCol ? [...display].sort((a,b)=>{ const av=sortCol.val(a), bv=sortCol.val(b);
      return (av<bv?-1:av>bv?1:0)*sort.dir; }) : display;
    const toggleSort = k => setSort(s => s.key===k ? {key:k,dir:-s.dir} : {key:k,dir:1});

    return (
      <div style={{minHeight:"100vh",background:BG,fontFamily:"sans-serif"}}>
        <div style={{background:DARK,padding:"12px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <NMLogo size={36}/>
            <span style={{color:"#fff",fontSize:16,fontWeight:700}}>NM Squad CRM</span>
            <span style={{background:"#374151",color:"#9ca3af",fontSize:11,padding:"2px 9px",borderRadius:4}}>Live · Firebase</span>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:12,color:"#9ca3af"}}>{inbox.length} active · {done.length} closed</span>
            <button onClick={()=>setView("tuap")} style={{fontSize:12,fontWeight:800,padding:"6px 12px",borderRadius:6,background:Y,color:YD,border:"none",cursor:"pointer"}}>
              TUAP Students
            </button>
            <button onClick={()=>setView("partners")} style={{fontSize:12,fontWeight:800,padding:"6px 12px",borderRadius:6,background:"#374151",color:"#fff",border:"none",cursor:"pointer"}}>
              Trusted Partners
            </button>
            <Btn onClick={()=>{localStorage.removeItem("nmAdminAuth");setView("welcome");if(unsubRef.current){unsubRef.current();unsubRef.current=null;}}} color="outline" size="sm">Logout</Btn>
            <div style={{display:'flex',alignItems:'center',gap:10,background:'#374151',padding:'6px 14px',borderRadius:8}}>
              <span style={{fontSize:12,color:'#9ca3af'}}>WhatsApp Auto-Send</span>
              <button
                onClick={()=>setWhatsappAutoSend(waSettings.autoSend?false:true)}
                style={{width:44,height:24,borderRadius:12,background:waSettings.autoSend?'#25D366':'#6b7280',cursor:'pointer',position:'relative',flexShrink:0,border:'none',padding:0,outline:'none'}}>
                <div style={{width:18,height:18,borderRadius:'50%',background:'#fff',position:'absolute',top:3,left:waSettings.autoSend?23:3,transition:'left .2s'}}/>
              </button>
              <span style={{fontSize:11,color:waSettings.autoSend?'#25D366':'#9ca3af',fontWeight:700}}>
                {waSettings.status==='connected'?'Connected':waSettings.status==='qr_needed'?'Scan QR':'Bot offline'}</span><button onClick={()=>setShowQR(true)} style={{fontSize:11,padding:"4px 10px",background:"#4b5563",color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontWeight:600}}>{waSettings.status==='qr_needed'?'Show QR':'Link WA'}</button><span style={{display:'none'}}>x}
              </span>
            </div>
          </div>
        </div>

        <div style={{background:"#fff",borderBottom:`1px solid ${BORDER}`,padding:"0 22px",display:"flex",flexWrap:"wrap",alignItems:"center"}}>
          {[["inbox","📥 Inbox"],["done","✅ Completed"]].map(([tab,label])=>(
            <button key={tab} onClick={()=>setAdTab(tab)} style={{padding:"14px 22px",fontSize:14,fontWeight:700,background:"none",border:"none",cursor:"pointer",color:adTab===tab?BLACK:"#9ca3af",borderBottom:adTab===tab?`3px solid ${Y}`:"3px solid transparent"}}>
              {label} <span style={{background:adTab===tab?Y:"#e5e7eb",color:adTab===tab?YD:MID,borderRadius:10,padding:"2px 8px",fontSize:11,fontWeight:700,marginLeft:4}}>
                {tab==="inbox"?inbox.length:done.length}
              </span>
            </button>
          ))}
        </div>
        <div style={{background:"#fff",borderBottom:`1px solid ${BORDER}`,padding:"12px 22px"}}>
          <input
            type="text"
            placeholder="Search by token, name, phone, email, school, city, status, comments..."
            value={searchQ}
            onChange={e=>setSearchQ(e.target.value)}
            style={{width:"100%",padding:"10px 14px",fontSize:14,border:`1px solid ${BORDER}`,borderRadius:8,outline:"none",boxSizing:"border-box",background:"#fff",color:BLACK,fontFamily:"inherit"}}
          />
        </div>

        <div style={{padding:mob?"14px":"22px 26px",maxWidth:1200,margin:"0 auto"}}>
          {showQR&&(
  <div onClick={()=>setShowQR(false)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}>
    <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,padding:32,textAlign:"center",maxWidth:380,width:"90%"}}>
      <h3 style={{margin:"0 0 4px",fontSize:18,fontWeight:800}}>WhatsApp Login</h3>
      {waSettings.connectedNumber&&<div style={{fontSize:12,color:"#15803d",fontWeight:700,marginBottom:12,padding:"4px 12px",background:"#dcfce7",borderRadius:20,display:"inline-block"}}>+{waSettings.connectedNumber} is logged in</div>}
      {waSettings.status==="connected"?(
        <div>
          <p style={{fontSize:13,color:"#6b7280",margin:"0 0 20px"}}>WhatsApp is connected. To switch to a different number, log out first.</p>
          <button onClick={()=>{logoutWhatsapp();setShowQR(false);}} style={{padding:"10px 24px",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:14}}>Logout WhatsApp</button>
        </div>
      ):(
        <div>
          <p style={{fontSize:12,color:"#6b7280",margin:"0 0 12px"}}>Open WhatsApp on phone: Settings > Linked Devices > Link a Device > Scan</p>
          {waSettings.qrCode?(
            <div style={{textAlign:"center"}}>
              <img src={waSettings.qrCode} alt="Scan QR" style={{width:260,height:260,borderRadius:8,border:"2px solid #e5e7eb"}}/>
              <p style={{fontSize:11,color:"#ef4444",fontWeight:700,marginTop:8}}>Scan within 25 seconds - auto-refreshes</p>
            </div>
          ):(
            <div style={{width:260,height:260,display:"flex",alignItems:"center",justifyContent:"center",background:"#f9fafb",borderRadius:8,margin:"0 auto",border:"2px dashed #e5e7eb"}}>
              <p style={{color:"#6b7280",fontSize:13,padding:16,textAlign:"center"}}>{waSettings.status==="connected"?"Already connected! No QR needed.":"Start the bot first, then come back here."}</p>
            </div>
          )}
        </div>
      )}
      <button onClick={()=>setShowQR(false)} style={{marginTop:16,padding:"8px 24px",background:"#111",color:"#fff",border:"none",borderRadius:8,fontWeight:700,cursor:"pointer"}}>Close</button>
    </div>
  </div>
)}<h2 style={{margin:"0 0 4px",fontSize:20,fontWeight:800,color:BLACK}}>Lead Intelligence Dashboard</h2>
          <p style={{margin:"0 0 20px",fontSize:14,color:MID}}>
            {leads.length===0?"Connecting to Firebase…":`${leads.length} total leads · Real-time sync ✓`}
          </p>

          {display.length===0?(
            <Card style={{textAlign:"center",padding:48}}>
              <NMLogo size={52}/>
              <p style={{color:SUB,marginTop:14,fontSize:14}}>{adTab==="inbox"?"No active leads.":"No completed leads."}</p>
            </Card>
          ):(
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",background:"#fff",borderRadius:12,overflow:"hidden",border:`1px solid ${BORDER}`,minWidth:780}}>
                <thead>
                  <tr style={{background:"#f9fafb"}}>
                    {COLS.map(c=>{ const on=sort.key===c.key; return (
                      <th key={c.key} onClick={()=>toggleSort(c.key)} title="Click to sort"
                        style={{padding:"7px 10px",textAlign:"left",fontSize:10,fontWeight:700,color:on?YD:MID,textTransform:"uppercase",letterSpacing:".04em",borderBottom:`1px solid ${BORDER}`,whiteSpace:"nowrap",cursor:"pointer",userSelect:"none",background:on?"#fffbeb":"transparent"}}>
                        {c.label}<span style={{marginLeft:4,opacity:on?1:.35,fontSize:9}}>{on?(sort.dir>0?"▲":"▼"):"↕"}</span>
                      </th>
                    );})}
                    <th style={{padding:"7px 10px",borderBottom:`1px solid ${BORDER}`}}></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((lead,i)=>{
                    const st  = lead.status||"Fresh Enquiry";
                    const sc2 = statusColor(st);
                    const fid = lead.firebaseId;
                    const dt  = lead.createdAt
                      ? new Date(lead.createdAt.seconds*1000).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"2-digit",hour:"2-digit",minute:"2-digit"})
                      : "—";
                    const TD = ({children,w,bold}) => (
                      <td style={{padding:"3px 10px",fontSize:12,color:bold?BLACK:MID,fontWeight:bold?600:400,whiteSpace:"nowrap",maxWidth:w||170,overflow:"hidden",textOverflow:"ellipsis"}}>
                        {children||"—"}
                      </td>
                    );
                    return (
                      <tr key={fid||i} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"#fff":"#fafafa"}}>
                        <td style={{padding:"4px 8px",textAlign:"center",minWidth:72,verticalAlign:"middle"}}>
                          {lead.whatsappSent===true?(
                            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                              <div style={{width:10,height:10,borderRadius:"50%",background:"#22c55e"}}/>
                              <span style={{fontSize:10,color:"#15803d",fontWeight:700}}>SENT</span>
                            </div>
                          ):lead.whatsappSent===false?(
                            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                              <div style={{width:10,height:10,borderRadius:"50%",background:"#ef4444"}}/>
                              <span style={{fontSize:10,color:"#dc2626",fontWeight:700}}>NOT SENT</span>
                              <button onClick={()=>sendWhatsApp(fid)} disabled={!!sendingWA[fid]||waSettings.status!=="connected"} style={{fontSize:10,padding:"3px 10px",background:sendingWA[fid]?"#9ca3af":"#25D366",color:"#fff",border:"none",borderRadius:5,cursor:"pointer",fontWeight:700,minWidth:56,marginTop:2}}>{sendingWA[fid]?"Sending...":"Send"}</button>
                            </div>
                          ):(
                            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                              <div style={{width:10,height:10,borderRadius:"50%",background:"#f59e0b"}}/>
                              <span style={{fontSize:10,color:"#92400e",fontWeight:700}}>PENDING</span>
                              <button onClick={()=>sendWhatsApp(fid)} disabled={!!sendingWA[fid]||waSettings.status!=="connected"} style={{fontSize:10,padding:"3px 10px",background:sendingWA[fid]?"#9ca3af":"#25D366",color:"#fff",border:"none",borderRadius:5,cursor:"pointer",fontWeight:700,minWidth:56,marginTop:2}}>{sendingWA[fid]?"Sending...":"Send"}</button>
                            </div>
                          )}
                        </td>
                        <td style={{padding:"3px 10px",fontSize:11,color:MID,whiteSpace:"nowrap"}}>{dt}</td>
                        <TD bold>{lead.studentName}</TD>
                        <TD>{(lead.country||"—").replace(/\s[\S]+$/,"")}</TD>
                        <TD>{lead.gender||"—"}</TD>
                        <TD>{lead.school}</TD>
                        <TD>{lead.referral}</TD>
                        <td style={{padding:"3px 10px",minWidth:138}}>
                          <select value={st} onChange={e=>updStatus(fid,e.target.value)}
                            style={{fontSize:11,fontWeight:700,color:sc2,background:"#fff",border:`2px solid ${sc2}`,borderRadius:6,padding:"3px 7px",cursor:"pointer",outline:"none",maxWidth:150}}>
                            {STATUS_OPS.map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{padding:"3px 10px",minWidth:150}}>
                          <input value={lead.comments||""} onChange={e=>updComment(fid,e.target.value)} placeholder="Notes…"
                            style={{width:"100%",fontSize:11,border:`1px solid ${BORDER}`,borderRadius:6,padding:"4px 8px",outline:"none",background:"#f9fafb",color:BLACK,boxSizing:"border-box"}}/>
                        </td>
                        <td style={{padding:"3px 8px"}}>
                          <div style={{display:"flex",gap:5,flexWrap:"nowrap",alignItems:"center"}}>
                            <button onClick={()=>{setDetailId(fid);setAiData(lead.aiInsights||null);setView("adminDetail");}}
                              style={{padding:"5px 10px",background:Y,border:"none",borderRadius:6,fontSize:11,fontWeight:700,color:YD,cursor:"pointer",whiteSpace:"nowrap"}}>
                              View →
                            </button>
                            {/* Convert to TUAP + Register for NTT only once a lead reaches the Completed section. */}
                            {adTab==="done"&&(<>
                              <button onClick={()=>convertToTUAP(lead)}
                                style={{padding:"5px 10px",background:lead.convertedToTuap?"#dcfce7":"#eef6ff",border:`1px solid ${lead.convertedToTuap?"#bbf7d0":"#bfdbfe"}`,borderRadius:6,fontSize:11,fontWeight:700,color:lead.convertedToTuap?"#15803d":"#1e40af",cursor:"pointer",whiteSpace:"nowrap"}}>
                                {lead.convertedToTuap?"Converted":"Convert to TUAP"}
                              </button>
                              <button onClick={()=>registerNTT(lead)} disabled={!!nttBusy[fid]}
                                title="Email the student + parent a secure link to take the NTT online"
                                style={{padding:"5px 10px",borderRadius:6,fontSize:11,fontWeight:700,whiteSpace:"nowrap",cursor:nttBusy[fid]?"default":"pointer",opacity:nttBusy[fid]?.6:1,
                                  ...(lead.nttStatus==="completed"?{background:"#dcfce7",border:"1px solid #bbf7d0",color:"#15803d"}
                                    :lead.nttStatus==="in_progress"?{background:"#fef9c3",border:"1px solid #fde68a",color:"#92400e"}
                                    :lead.nttStatus==="registered"?{background:"#ede9fe",border:"1px solid #ddd6fe",color:"#6d28d9"}
                                    :{background:"#fff7ed",border:"1px solid #fed7aa",color:"#9a3412"})}}>
                                {nttBusy[fid]?"Sending…"
                                  :lead.nttStatus==="completed"?"NTT done"
                                  :lead.nttStatus==="in_progress"?"NTT started"
                                  :lead.nttStatus==="registered"?"NTT sent — resend"
                                  :"Register for NTT"}
                              </button>
                            </>)}
                            <button onClick={()=>delLead(fid)}
                              style={{padding:"5px 8px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:6,fontSize:11,fontWeight:700,color:"#dc2626",cursor:"pointer"}}>
                              🗑
                            </button>
                            {adTab==="done"&&(
                              <button onClick={()=>updStatus(fid,"Connected")}
                                style={{padding:"5px 8px",background:"#f3f4f6",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:11,fontWeight:600,color:MID,cursor:"pointer"}}>
                                ↩
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ADMIN DETAIL */
  if (view==="adminDetail") {
    const lead = leads.find(l=>l.firebaseId===detailId);
    if (!lead) return (
      <div style={{padding:28,fontFamily:"sans-serif"}}>
        <button onClick={()=>setView("admin")} style={{fontSize:14,color:MID,background:"none",border:"none",cursor:"pointer"}}>← Back</button>
        <p>Lead not found. It may still be loading.</p>
      </div>
    );

    const cls  = classifyLead(lead.conversionScore||0);
    const st   = lead.status||"Fresh Enquiry";
    const sc2  = statusColor(st);
    const ai   = aiData;
    const fid  = lead.firebaseId;
    const cWa  = `https://wa.me/${(lead.phone||"").replace(/\D/g,"").length===10?"91"+(lead.phone||"").replace(/\D/g,""):(lead.phone||"").replace(/\D/g,"")}?text=${encodeURIComponent(`Hello, this is Rajib from NM Squad. Thank you for filling out the form. I have had a quick look at your profile (Ref: ${lead.token}) and have shared your report as a PDF here. I look forward to speaking with you and helping you schedule the earliest possible appointment with Dr. Neeraj Mandhana.`)}`;

    const runAI = async() => {
      setAiLoad(true);
      try {
        const sub = { info:lead, scores:{IQ:lead.IQ,EQ:lead.EQ,DQ:lead.DQ,AQ:lead.AQ,dominant:lead.dominantProfile}, leadScores:{conversionScore:lead.conversionScore,personalScore:lead.personalScore,professionalScore:100-(lead.personalScore||0)}, interests:lead.interestAnswers||{}, parentAns:lead.parentAnswers||{} };
        const result = await generateAI(sub);
        setAiData(result);
        await saveAIInsights(fid, result);
        setLeads(prev=>prev.map(l=>l.firebaseId===fid?{...l,aiInsights:result}:l));
      } catch(e) { alert("AI analysis failed: "+e.message); }
      setAiLoad(false);
    };

    const scoreList2 = [
      {label:"IQ",sub:"Analytical",score:lead.IQ||0,color:"#2563eb",bg:"#eff6ff"},
      {label:"EQ",sub:"Emotional", score:lead.EQ||0,color:"#ea580c",bg:"#fff7ed"},
      {label:"DQ",sub:"Creative",  score:lead.DQ||0,color:"#16a34a",bg:"#f0fdf4"},
      {label:"AQ",sub:"Physical",  score:lead.AQ||0,color:"#7c3aed",bg:"#faf5ff"},
    ];
    const NTT_WS_NAMES = {sd:"Spot the Match",type:"Fast Fingers",word:"First Word",idea:"Idea Storm",pairs:"Word Pairs",design:"Pattern Recall",fold:"Fold & Find"};
    const nttFin = lead.nttCompletedAt;

    return (
      <div style={{minHeight:"100vh",background:BG,fontFamily:"sans-serif"}}>
        <style>{PCSS}</style>
        <div className="np" style={{background:DARK,padding:"12px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <button onClick={()=>setView("admin")} style={{color:"#9ca3af",background:"none",border:"none",cursor:"pointer",fontSize:14}}>← All leads</button>
            <span style={{color:"#fff",fontSize:15,fontWeight:700}}>{lead.studentName}</span>
            <span style={{padding:"3px 9px",background:cls.bg,color:cls.color,borderRadius:5,fontSize:12,fontWeight:700}}>{cls.emoji} {cls.label}</span>
            <span style={{padding:"3px 9px",background:"#fffbeb",color:YD,borderRadius:5,fontSize:12,fontWeight:700}}>{lead.token}</span>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {lead.convertedToTuap ? (
              <span style={{padding:"8px 14px",background:"#065f46",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:5}}>✓ TUAP · Ravisha notified</span>
            ) : (
              <button onClick={()=>convertToTUAP(lead)}
                style={{padding:"8px 14px",background:"#2563eb",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,border:"none",cursor:"pointer"}}>
                Convert to TUAP
              </button>
            )}
            <a href={cWa} target="_blank" rel="noopener noreferrer" style={{padding:"8px 14px",background:"#25D366",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,textDecoration:"none",display:"flex",alignItems:"center",gap:5}}>
              {WA_ICON}WhatsApp {lead.studentName?.split(" ")[0]}
            </a>
            <Btn onClick={()=>handlePdf("admin-detail-rpt",`Admin_${(lead.studentName||"").replace(/ /g,"_")}.pdf`)} color="yellow" size="sm">{pdfLoading?"…":"Download PDF"}</Btn>
          </div>
        </div>

        <div id="admin-detail-rpt" style={{maxWidth:960,margin:"0 auto",padding:mob?14:26}}>

          {/* Header */}
          <Card style={{border:`2px solid ${Y}`}}>
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
              <NMLogo size={48}/>
              <div>
                <p style={{margin:0,fontSize:10,color:SUB,textTransform:"uppercase",letterSpacing:".1em"}}>NM Squad · Admin View</p>
                <h1 style={{margin:0,fontSize:mob?18:22,fontWeight:800,color:BLACK}}>First Meeting Enquiry — {lead.studentName}</h1>
              </div>
              <div style={{marginLeft:"auto",textAlign:"right"}}>
                <p style={{margin:0,fontSize:10,color:SUB,textTransform:"uppercase"}}>Token</p>
                <p style={{margin:0,fontSize:24,fontWeight:900,color:YD}}>{lead.token}</p>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",borderTop:`1px solid ${BORDER}`,paddingTop:14,justifyContent:"space-between"}}>
              <div style={{display:"flex",gap:18,flexWrap:"wrap"}}>
                {[["Student",lead.studentName],["Email",lead.email],["Phone",lead.phone],["Parent Email",lead.parentEmail],["Parent Phone",lead.parentPhone],["School",lead.school],["Country",lead.country],["City",lead.city],["Grade",lead.grade],["Referral",lead.referral],["Lead person",lead.leadPerson+(lead.leadPersonOther?` (${lead.leadPersonOther})`:"")]].map(([k,v])=>(
                  <div key={k}><p style={{margin:0,fontSize:10,color:SUB}}>{k}</p><p style={{margin:"2px 0 0",fontSize:13,fontWeight:700,color:BLACK}}>{v||"—"}</p></div>
                ))}
              </div>
              <div>
                <p style={{margin:"0 0 5px",fontSize:11,color:SUB}}>STATUS</p>
                <select value={st} onChange={e=>updStatus(fid,e.target.value)}
                  style={{fontSize:13,fontWeight:700,color:sc2,background:"#fff",border:`2px solid ${sc2}`,borderRadius:7,padding:"7px 12px",cursor:"pointer",outline:"none"}}>
                  {STATUS_OPS.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </Card>

          {/* Natural Talent Test — online test status + raw results */}
          <Card>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
              <div>
                <p style={{margin:0,fontSize:10,color:SUB,textTransform:"uppercase",letterSpacing:".1em"}}>Natural Talent Test</p>
                <h2 style={{margin:"2px 0 0",fontSize:mob?16:18,fontWeight:800,color:BLACK}}>
                  {lead.nttStatus==="completed"?"Completed"
                    :lead.nttStatus==="in_progress"?"In progress"
                    :lead.nttStatus==="registered"?"Link sent — not started yet"
                    :"Not registered"}
                </h2>
                {nttFin&&(<p style={{margin:"3px 0 0",fontSize:12,color:SUB}}>Finished {new Date(nttFin.seconds*1000).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</p>)}
              </div>
              {(DONE_S.includes(st)||lead.nttStatus)&&(
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button onClick={()=>registerNTT(lead)} disabled={!!nttBusy[fid]}
                    style={{padding:"8px 14px",borderRadius:8,fontSize:13,fontWeight:700,border:"none",background:Y,color:YD,cursor:nttBusy[fid]?"default":"pointer",opacity:nttBusy[fid]?.6:1}}>
                    {nttBusy[fid]?"Sending…":lead.nttStatus?"Re-send link":"Register for NTT"}
                  </button>
                  {lead.nttToken&&(
                    <a href={nttTestLink(lead)} target="_blank" rel="noopener noreferrer"
                      style={{padding:"8px 14px",borderRadius:8,fontSize:13,fontWeight:700,background:"#fff",border:`1px solid ${BORDER}`,color:MID,textDecoration:"none"}}>Open test link</a>
                  )}
                </div>
              )}
            </div>
            {lead.nttResults&&(
              <div style={{marginTop:14,borderTop:`1px solid ${BORDER}`,paddingTop:12}}>
                <p style={{margin:"0 0 8px",fontSize:11,color:SUB,textTransform:"uppercase",letterSpacing:".06em"}}>Worksamples completed ({Object.keys(lead.nttResults).length} of 7)</p>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {Object.keys(lead.nttResults).map(k=>(
                    <span key={k} style={{padding:"4px 10px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:6,fontSize:11,fontWeight:700,color:"#15803d"}}>{NTT_WS_NAMES[k]||k}</span>
                  ))}
                </div>
                <p style={{margin:"10px 0 0",fontSize:11,color:SUB,fontStyle:"italic"}}>Raw responses are stored for the scoring + report engine. The full talent report and counsellor debrief will be generated here.</p>
              </div>
            )}
          </Card>

          {/* Conversion + Scores */}
          <div style={{display:"grid",gridTemplateColumns:"1fr",gap:14,marginBottom:14}}>
            <Card style={{marginBottom:0,background:"#fffbeb",border:`2px solid ${Y}`}}>
              <h3 style={{margin:"0 0 12px",fontSize:16,fontWeight:800,color:YD}}>Fit with NM Squad</h3>
              <div style={{textAlign:"center",marginBottom:16}}>
                <div style={{fontSize:58,fontWeight:900,color:cls.color,lineHeight:1}}>{lead.conversionScore||0}%</div>
                
              </div>
              <div style={{marginTop:8}}>
                <div style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                    <span style={{fontSize:13,fontWeight:700,color:YD}}>Personal</span>
                    <span style={{fontSize:16,fontWeight:900,color:YD}}>{lead.personalScore||0}%</span>
                  </div>
                  <div style={{height:16,background:"#fffbeb",borderRadius:9999,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${lead.personalScore||0}%`,background:Y,borderRadius:9999}}/>
                  </div>
                </div>
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                    <span style={{fontSize:13,fontWeight:700,color:MID}}>Professional</span>
                    <span style={{fontSize:16,fontWeight:900,color:MID}}>{lead.professionalScore||0}%</span>
                  </div>
                  <div style={{height:16,background:"#f3f4f6",borderRadius:9999,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${lead.professionalScore||0}%`,background:MID,borderRadius:9999}}/>
                  </div>
                </div>
              </div>
            </Card>

          </div>

          {/* Student Profile & Analysis */}
          <Card style={{border:"2px solid #F5C518",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
              <span style={{fontSize:20}}>📊</span>
              <h3 style={{margin:0,fontSize:16,fontWeight:800,color:BLACK}}>Student Profile Analysis</h3>
              <span style={{fontSize:12,color:SUB,fontStyle:"italic"}}>auto-generated · internal only</span>
            </div>

            {/* Score bars matching report */}
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
                <span style={{fontSize:14,fontWeight:700,color:"#2563eb"}}>Data, Logic, Machines</span>
                <span style={{fontSize:20,fontWeight:900,color:"#2563eb"}}>{lead.IQ||0}%</span>
              </div>
              <div style={{height:22,background:"#eff6ff",borderRadius:9999,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${lead.IQ||0}%`,background:"#2563eb",borderRadius:9999}}/>
              </div>
              <div style={{fontSize:11,color:SUB,marginTop:3}}>IQ — Analytical thinking, data and systems</div>
            </div>
            <div style={{marginBottom:20}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
                <span style={{fontSize:14,fontWeight:700,color:"#ea580c"}}>People, Communication, Emotion</span>
                <span style={{fontSize:20,fontWeight:900,color:"#ea580c"}}>{(lead.EQ||0)+(lead.DQ||0)}%</span>
              </div>
              <div style={{height:22,background:"#fff7ed",borderRadius:9999,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${(lead.EQ||0)+(lead.DQ||0)}%`,background:"#ea580c",borderRadius:9999}}/>
              </div>
              <div style={{fontSize:11,color:SUB,marginTop:3}}>EQ + DQ — Social intelligence and creative expression</div>
            </div>

            {/* Profile summary */}
            {(() => {
              const iq=lead.IQ||0, ppl=(lead.EQ||0)+(lead.DQ||0);
              const dom = iq > ppl ? 'Data, Logic & Machines' : 'People, Communication & Emotion';
              const domPct = iq > ppl ? iq : ppl;
              const orientation = iq > ppl ? 'STEM, Data Science, Engineering, Technology, Finance' : 'Arts, Communication, Social Sciences, Creative Fields, Education';
              const studType = iq > 60 ? 'Analytical Thinker — prefers structured problems, data-driven decisions, and systematic approaches.' : ppl > 60 ? 'People-Oriented — empathetic, communicative, creative, and socially aware.' : 'Balanced — capable across both analytical and social domains.';
              const topInts = Object.entries(lead.interestAnswers||{}).filter(([k,v])=>v==='interested').map(([k])=>k);
              return (
                <div>
                  {topInts.length > 0 && (
                    <div style={{background:"#F5C518",border:"none",borderRadius:8,padding:"12px 14px"}}>
                      <div style={{fontSize:11,fontWeight:700,color:YD,textTransform:"uppercase",marginBottom:8}}>Top Interests (Interested)</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                        {topInts.map((t,i)=>(
                          <span key={i} style={{padding:"4px 10px",background:"#fff",border:`1px solid ${Y}`,borderRadius:16,fontSize:11,color:YD,fontWeight:600,whiteSpace:"nowrap"}}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </Card>

          {/* All Interest Responses */}
          <Card>
            <h3 style={{margin:"0 0 14px",fontSize:16,fontWeight:800,color:BLACK}}>Full Interest Survey Responses</h3>
            <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"repeat(3,1fr)",gap:8}}>
              {Object.entries(lead.interestAnswers||{}).sort().map(([activity,response])=>{
                const col = response==="interested"?"#15803d":response==="maybe"?"#ca8a04":"#9ca3af";
                const bg  = response==="interested"?"#f0fdf4":response==="maybe"?"#fffbeb":"#f9fafb";
                return (
                  <div key={activity} style={{background:bg,border:`1px solid ${response==="interested"?"#bbf7d0":response==="maybe"?"#fde68a":"#e5e7eb"}`,borderRadius:7,padding:"8px 11px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:12,color:BLACK,fontWeight:500}}>{activity}</span>
                    <span style={{fontSize:11,fontWeight:700,color:col,textTransform:"capitalize",marginLeft:6,flexShrink:0}}>{response}</span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Parent responses */}
          <Card>
            <h3 style={{margin:"0 0 14px",fontSize:16,fontWeight:800,color:BLACK}}>Parent Psychology Responses</h3>
            <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:10}}>
              {PARENT_QS.map(pq=>{
                const ans = (lead.parentAnswers||{})[pq.id];
                const opt = pq.opts?.find(o=>o.text===ans);
                return (
                  <div key={pq.id} style={{background:"#f9fafb",borderRadius:8,padding:"12px 14px"}}>
                    <div style={{fontSize:11,color:SUB,marginBottom:5,lineHeight:1.4,fontWeight:500}}>{pq.q}</div>
                    <div style={{fontSize:14,color:BLACK,fontWeight:600}}>{ans||"—"}</div>
                    {opt&&pq.scored&&<span style={{marginTop:5,display:"inline-block",fontSize:11,padding:"2px 8px",background:opt.type==="personal"?"#fffbeb":"#f0f9ff",color:opt.type==="personal"?YD:"#0369a1",borderRadius:4,fontWeight:700}}>{opt.type}</span>}
                    {!pq.scored&&<span style={{marginTop:5,display:"inline-block",fontSize:11,padding:"2px 8px",background:"#f3f4f6",color:SUB,borderRadius:4}}>info only</span>}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Call notes */}
          <Card>
            <h3 style={{margin:"0 0 10px",fontSize:16,fontWeight:800,color:BLACK}}>Call Notes (Rajib)</h3>
            <textarea value={lead.comments||""} onChange={e=>updComment(fid,e.target.value)}
              placeholder="Add call notes, parent observations, objections, follow-up actions…"
              style={{width:"100%",minHeight:110,boxSizing:"border-box",fontSize:14,border:`1px solid ${BORDER}`,borderRadius:8,padding:"13px 16px",outline:"none",resize:"vertical",fontFamily:"sans-serif",color:BLACK,background:"#f9fafb",lineHeight:1.7}}/>
          </Card>

          <p style={{textAlign:"center",fontSize:11,color:"#d1d5db",padding:"10px 0 8px"}}>
            NM Squad Admin · {lead.createdAt?new Date(lead.createdAt.seconds*1000).toLocaleDateString():"—"} · Token: {lead.token}
          </p>
        </div>
      </div>
    );
  }

  return null;
}
